import {
  normalizeSourceTextUpload,
  SOURCE_TEXT_EXTENSIONS,
  SOURCE_TEXT_MAX_BYTES,
} from '@oncatch/types/source-text';
import { mountAssistant } from './assistant.ts';
// Widget UI: shadow DOM mount + draggable icon + slide-out panel + form.
// Inlined CSS — the entire widget is self-contained; no external styles leak in or out.
//
// #161 (#5a, Phase 1): adds up to 5 in-browser screenshots between the Details
// textarea and the reporter fields. TWO browser-only capture affordances:
//   - "Whole page": one click captures the entire current viewport via
//     html-to-image (~25KB chunk inlined into the IIFE bundle today)
//   - "Select region": fullscreen crosshair overlay → user drags rectangle →
//     captures full page then crops to the selection in canvas
// Multiple snapshots support a "step-by-step" sequence the user assembles by
// repeating the action.
//
// Scope history:
//   - 2026-04-30 pivot: file picker, drag-and-drop, paste, and getDisplayMedia
//     were all excluded — "bug on this page" vs "generic file upload" felt
//     like the wrong mental model for a bug-report widget. (Screen recording
//     itself later shipped separately via #163's MediaRecorder flow.)
//   - #605 reverses the first three FOR IMAGES ONLY: reporters routinely
//     already have the evidence (an OS screenshot taken when the bug
//     happened, a phone photo of another screen) and the page has since
//     changed, so live capture can't reproduce it. A dropzone below the
//     capture buttons accepts picker / drop / paste of png|jpeg|webp,
//     feeding the same addFile() pipeline (WITH WebP compression — uploads
//     are not widget-generated PNGs). Hidden and inert under
//     sensitiveDataMode: uploads bypass privacy-mask.ts, and that workspace
//     opted out of exactly that risk.
//   - Still excluded: getDisplayMedia frame-grab, video uploads, non-image
//     documents (PDF/logs) — see #605 "Out of scope".

import type { AnnotationOutcome } from './annotation.ts';
import { runAnnotationFlow } from './annotation.ts';
import type { AttachmentManager, AttachmentSlot } from './attachments.ts';
import { AttachmentValidationError, MAX_PRE_COMPRESSION_BYTES } from './attachments.ts';
import type { CaptureAvailability, CaptureChoiceAvailability } from './capture-choice-modal.ts';
import {
  clearStoredContact,
  isContactStorageAvailable,
  readStoredContact,
  writeStoredContact,
} from './contact-prefill.ts';
// #482: Trusted Types bridge — see csp.ts. Routes the widget's static markup
// blocks through a feature-detected TT policy so a host CSP enforcing
// `require-trusted-types-for 'script'` can't crash the mount via innerHTML.
import { adoptStyles, setTrustedHtml } from './csp.ts';
import { type DictationUi, installDictation, type TranscribeDraft } from './dictation.ts';
import { launchHostedRecorder } from './hosted-recorder-launcher.ts';
import type { SupportedLocale, WidgetMessages } from './i18n/index.ts';
// #212 PR-B — render-time read of the current identity store. ui.ts pulls
// the state directly rather than via UiHandlers because the form HTML is
// re-rendered on each open and identity may have changed between opens.
import { getCurrentIdentity, isIdentificationActive } from './identity.ts';
import {
  launcherRightInset,
  OVERLAY_SCROLLBAR_CLEARANCE,
  overlayScrollbarCoversEdge,
} from './launcher-edge.ts';
// #533 — submit-flow instrumentation. Guarded no-ops when the User Timing API
// is unavailable, so instrumentation can never affect widget behavior.
import { mark, measure, WidgetMark } from './perf.ts';
import { PinController, type PinnedData } from './pin-controller.ts';
import { applyScreenshotMask, combineMaskSelectors } from './privacy-mask.ts';
// #332 — viewport-only region-capture geometry. The pure math lives in its
// own module so it can be unit-tested without a DOM. #329 added the rasterize +
// crop + blank-guard routine alongside it, so the real-browser regression
// harness exercises the shipping code rather than a copy.
import { renderRegionCapture } from './region-capture.ts';
import {
  chooseRibbonEdge,
  parseRibbonDock,
  type RibbonDock,
  type RibbonEdge,
  ribbonPlacement,
} from './ribbon-dock.ts';

// #215 — Active pin capture data. Set by PinController.onCapture when the
// reporter pins an element; read by the submit handler when assembling the
// SubmittedForm; cleared after a successful submit. Module-level state
// because the widget is a singleton per host page (one mount).
let activePin: PinnedData | null = null;
const dictationForms = new WeakMap<HTMLElement, DictationUi>();

export interface UiHandlers {
  dictationEnabled?: boolean;
  transcribeDraft?: TranscribeDraft;
  onOpen: () => void;
  onSubmit: (form: SubmittedForm) => Promise<{
    ok: boolean;
    referenceCode?: string;
    error?: string;
    /**
     * #256 — count of attachments the server's reconcile loop dropped. When
     * > 0 the UI surfaces a partial-success notice in addition to the
     * reference code. `attachmentsTotal` is the count of attachments the
     * widget submitted. Both omitted on stale clients or when no attachments
     * were submitted (zero-failed = clean success).
     */
    attachmentsFailed?: number;
    attachmentsTotal?: number;
  }>;
  onClose: () => void;
  /**
   * #56: i18n. `locale` drives the shadow-host `lang` attribute and the
   * Turnstile `data-language` override; `messages` is the typed message
   * dictionary every reporter-facing surface reads from. Resolved once at
   * bootstrap from the widget-config / script-tag / browser-preference
   * priority chain.
   */
  locale: SupportedLocale;
  messages: WidgetMessages;
  /**
   * #161 (#5a): optional. When provided, the form renders attachment slots and
   * the submit flow asks the bootstrap layer to forward reservation IDs to the
   * server. Omit to ship the form without attachment support.
   */
  attachmentManager?: AttachmentManager;
  /**
   * Workspace-level "sensitive data mode" flag. When `true`, the widget
   * (a) hides the screen-recording button outright (and skips the
   * MediaRecorder support check that would otherwise surface a Safari
   * banner), and (b) does not transmit console / network log snapshots
   * — bootstrap calls `disableCapture()` on the buffers module which
   * clears existing entries and short-circuits future writes. Reporters
   * can still submit a title, summary, and screenshots; everything else
   * is suppressed.
   */
  sensitiveDataMode?: boolean;
  /**
   * #648 (OC-5): workspace's reporter fix-ship notifications toggle from
   * /api/v1/widget-config. When true, the form renders the optional
   * "Notify me when this is fixed" checkbox, visible only while the email
   * field is non-empty. Default false (checkbox not rendered).
   */
  fixNotificationsEnabled?: boolean;
  /**
   * #136: storage cap state from /api/v1/widget-config. When 'full', the widget
   * hides the capture buttons and renders an "Attachments unavailable" message
   * inline — text-only submission still works. Default 'normal'.
   */
  storageState?: 'normal' | 'full';
  /**
   * #217: privacy masking config from /api/v1/widget-config. Workspace
   * arrays ADD to the widget's hardcoded defaults. Used by screenshot
   * capture (CSS selectors → visual blanking) and by the submit path
   * (regex patterns → string redaction in console + network logs).
   */
  privacyConfig?: {
    maskSelectors: string[];
    redactPatterns: string[];
  };
  /**
   * #241: workspace display name from /api/v1/widget-config. Used by the
   * recording-flow consent modal copy. Empty string falls back to the
   * generic "this site" copy.
   */
  workspaceName?: string;
  /**
   * Workspace id, used to key the opt-in reporter-contact prefill in
   * localStorage so two widgets on one origin never share saved contacts.
   * Optional: when absent the "Save your name and email" checkbox is not
   * rendered at all, which keeps legacy embed paths working unchanged.
   */
  workspaceId?: string;
  /**
   * #241: workspace's `attachment_retention_days` from /api/v1/widget-config.
   * Used by the recording-flow consent modal copy. Defaults to 90 if not
   * supplied (legacy embeds before the route extension shipped).
   */
  attachmentRetentionDays?: number;
  /** #774 — official widget origin hosting the top-level recorder page. */
  widgetBaseUrl?: string;
  /** Admin API used by the hosted page to claim its short-lived interaction. */
  apiBaseUrl?: string;
  /** Returns a fresh, in-memory submission token only after a reporter clicks. */
  getHostedRecorderToken?: () => Promise<string | null>;
  /**
   * #163 PR C: best-effort callback to ingest widget-side audit events (the
   * recording lifecycle: consent_accepted, started, stopped). Bootstrap wires
   * this to api.submitWidgetEvent so events land in auth_events with full
   * workspace + IP + UA context. Optional — when omitted (e.g., legacy
   * embeds), recording still works but no audit row gets logged.
   */
  onWidgetEvent?: (event: {
    type:
      | 'recording_consent_accepted'
      | 'recording_started'
      | 'recording_stopped'
      // #297 — emitted from the camera-recording flow when the user accepted
      // the screen pick but the camera stream failed (denied / no device /
      // mid-record failure). Partial-fulfillment marker, separate from
      // recording_stopped so the workspace owner can spot trends.
      | 'camera_denied_fallback';
    metadata?: Record<string, unknown>;
  }) => void;
  /**
   * Custom-launcher mode. When true, mountUi does NOT render its built-in
   * bug-button launcher — the host page supplies its own launcher (e.g. an
   * edge ribbon) and opens the widget via the returned `open()`. The panel
   * and open()/close() behave exactly as in default mode. Default false
   * (built-in launcher shown).
   */
  hideLauncher?: boolean;
  /** Same-origin host adapter; never an arbitrary remote model endpoint. */
  assistantEndpoint?: string;
  /**
   * #598 — deferred-config mode. When true, mountUi renders the launcher but
   * NOT the form: the panel body stays a loading placeholder until the first
   * open resolves `onFirstOpen`. This is what lets bootstrap mount the widget
   * with ZERO network calls on page load — /widget-config and /heartbeat move
   * to the first open, so a visitor who never touches the widget never reaches
   * our API and never has their IP written to `auth_events`.
   *
   * Requires `onFirstOpen`. Without it the flag is ignored (form renders
   * eagerly, pre-#598 behaviour).
   */
  deferInitialRender?: boolean;
  /**
   * #598 — resolves the real widget config on the reporter's first open.
   * Called at most once; the result is applied to this same handlers object
   * (see `MountedUi.applyConfig`) before the form renders for the first time.
   *
   * `ok: false` means the widget cannot be used on this origin — bootstrap
   * distinguishes `disabled` (workspace off / origin not allowlisted / rate
   * limited) from `offline` (the fetch itself failed) so the panel can explain
   * which, instead of the pre-#598 behaviour of no widget appearing at all
   * with only a console warning.
   */
  onFirstOpen?: () => Promise<
    { ok: true; patch?: Partial<UiHandlers> } | { ok: false; reason: 'disabled' | 'offline' }
  >;
}

// #210 — name of the CustomEvent dispatched on the shadow root when the
// user closes the widget panel (either via the bug-button toggle or the
// in-panel [×]). The annotation flow listens for this so it can settle
// as 'discard' when the panel closes mid-flow.
export const PANEL_CLOSE_EVENT = 'oncatch:panel-close';

export interface SubmittedForm {
  title: string;
  summary: string;
  reporterName: string;
  reporterEmail: string;
  // #648 (OC-5) — "Notify me when this is fixed" checkbox state, already
  // guarded at read time (true only when the email field is non-empty).
  notifyOnFix: boolean;
  // #215 — pin-to-element capture data attached by PinController when
  // the user submitted via pin mode. Undefined for screenshot / recording
  // submissions.
  pinned?: import('./pin-controller.ts').PinnedData;
}

// #343 / #799: Inter is fetched and registered by registerWidgetFont() in
// index.ts at boot time (not here) — the shadow DOM below only needs the
// font-family stack. Registration goes through `document.fonts` because
// that's the only font registry there is (a ShadowRoot has none of its own);
// #343 found that an `@font-face` rule inside the shadow root's own
// stylesheet never triggers a load in Chrome, which is why it isn't done
// that way.
function buildStyles(): string {
  return `
  .panel-header:has(.oc-assistant-toggle:not([hidden])) .panel-title { flex:0 1 auto; }
  .oc-assistant-toggle { margin-right:auto; flex:none; width:44px; height:26px; padding:3px; border:1px solid #a4977c; border-radius:20px; background:#e7e3da; cursor:pointer; margin-left:12px; }
  .oc-assistant-toggle span { display:block; width:18px; height:18px; border-radius:50%; background:white; box-shadow:0 1px 3px #0003; }
  .oc-assistant-toggle[aria-checked="true"] { background:#c73f00; }
  .oc-assistant-toggle[aria-checked="true"] span { transform:translateX(17px); }
  .oc-assistant { display:flex; flex-direction:column; min-height:420px; max-height:65vh; }
  .oc-assistant[hidden], .oc-assistant-toggle[hidden] { display:none; }
  .oc-assistant-messages { flex:1; min-height:180px; overflow:auto; padding:20px; white-space:pre-wrap; overflow-wrap:anywhere; }
  .oc-assistant-user { background:#f2eee6; border-radius:12px; padding:12px; margin-left:24px; }
  .oc-assistant-response { padding:4px 0; }
  .oc-assistant-compose { border-top:1px solid #a4977c; padding:16px; display:flex; flex-wrap:wrap; gap:8px; }
  .oc-assistant-compose textarea { width:100%; box-sizing:border-box; font:inherit; resize:vertical; border:1px solid #a4977c; border-radius:8px; padding:12px; }
  .oc-assistant-compose button { border:1px solid #c73f00; background:#c73f00; color:white; border-radius:6px; padding:8px 16px; cursor:pointer; }
  :host {
    /* Palette + typography aligned with the marketing site
       (oncatch-website/styles/oncatch.css) and the admin design system. */
    --oncatch-accent: #c2410c;        /* OnCatch rust */
    /* #55 contrast fix: lifted from #fbe5d6 (4.26:1) to #fdeede (4.55:1)
       so accent-on-accent-soft pairings (the success-state ref-code chip)
       clear WCAG AA's 4.5:1 body-text threshold. Visual impact is a slight
       paler tint of the rust-soft surface; perceptually within the same
       hue family. */
    --oncatch-accent-soft: #fdeede;
    --oncatch-bg: #ffffff;             /* widget panel stays white so it pops on customer bgs */
    --oncatch-fg: #1b1a17;             /* warm ink */
    --oncatch-muted: #6b6357;
    /* #55 contrast fix: darkened from #d9d2c2 (1.51:1) to #9b8f70 (3.20:1)
       so input/textarea borders clear WCAG 1.4.11's 3:1 UI-component
       threshold. Tried #8b7e63 (3.99:1) first — passed with margin but
       read too defined for the OnCatch "soft paper" brand. #9b8f70 still
       passes AA (just barely) with less visual weight. Future-watch:
       0.20 of margin is thin, so any future palette darkening on bg
       could regress this; re-run scripts/check-contrast-ratios.py
       before any palette change. */
    --oncatch-border: #9b8f70;
    all: initial;
    font-family: 'OnCatchUiFont', system-ui, -apple-system, "Segoe UI", Roboto, sans-serif !important;
  }
  /* Drag the side grip to reposition the ribbon. Minimize is a panel setting;
     hovering a minimized ribbon temporarily reveals the launcher. */
  .btn {
    box-sizing: border-box;
    max-width: 100vw;
    max-height: 100vh;
    position: fixed;
    right: 0;
    top: 40vh;
    z-index: 2147483646;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
    width: 30px;
    height: 112px;
    min-height: min(112px, 100vh);
    padding: 12px 0 0;
    background: var(--oncatch-accent);
    color: #fff;
    border: none;
    border-radius: 13px 0 0 13px;
    box-shadow: -5px 5px 18px rgba(27, 26, 23, 0.2);
    cursor: grab;
    user-select: none;
    -webkit-user-select: none;
    touch-action: none;
    transition: width 0.18s ease, box-shadow 0.18s ease, opacity 0.26s ease,
      transform 0.26s cubic-bezier(0.2, 0.7, 0.3, 1);
  }
  .btn:hover { box-shadow: -7px 6px 22px rgba(27, 26, 23, 0.26); }
  .btn:active, .btn.dragging { cursor: grabbing; }
  .btn.dragging { transition: none; opacity: 0.94; }
  .btn:focus-visible { outline: 3px solid #fff; outline-offset: -3px; }
  /* One hit target for the whole tab. */
  .btn * { pointer-events: none; }
  .btn .oc-lbl {
    flex: 1;
    min-width: 0;
    min-height: 0;
    text-align: center;
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.06em;
  }
  .btn[data-edge="left"] { flex-direction: column-reverse; padding: 0 0 12px; border-radius: 0 13px 13px 0; }
  .btn[data-edge="left"] .oc-lbl { transform: none; }
  .btn[data-edge="top"], .btn[data-edge="bottom"] {
    flex-direction: row-reverse; width: 112px; min-height: 0; height: 30px;
    padding: 0 12px 0 0; border-radius: 0 0 13px 13px;
  }
  .btn[data-edge="bottom"] { border-radius: 13px 13px 0 0; }
  .btn[data-edge="top"] .oc-lbl, .btn[data-edge="bottom"] .oc-lbl {
    writing-mode: horizontal-tb; transform: none;
  }
  /* Grip follows the text in reading order on every edge. */
  .btn .oc-grip { display: flex; flex-shrink: 0; }
  .btn .oc-dots {
    display: grid;
    grid-template-columns: 3px 3px 3px;
    gap: 3px;
  }
  .btn[data-edge="top"] .oc-dots, .btn[data-edge="bottom"] .oc-dots { grid-template-columns: 3px 3px; }
  .btn .oc-grip i { width: 3px; height: 3px; border-radius: 50%; background: rgba(255, 255, 255, 0.9); display: block; }
  /* Hairline: a few pixels of accent only, flush with the window edge.
     Peek removes this class. Children stay in flow so the bar keeps the full
     tab height, but they must not paint — a narrow clip was still showing
     white glyph slivers from the rotated label. */
  .btn.oc-min {
    width: 5px;
    /* Children are display:none, so this has to match .btn's min-height or
       the hairline paints taller than the open tab. */
    min-height: min(112px, 100vh);
    padding: 0;
    gap: 0;
    border-radius: 2px 0 0 2px;
    box-shadow: none;
    overflow: hidden;
    background: var(--oncatch-accent);
    color: var(--oncatch-accent);
  }
  .btn[data-edge="left"].oc-min { border-radius: 0 2px 2px 0; }
  .btn[data-edge="top"].oc-min, .btn[data-edge="bottom"].oc-min {
    width: 112px; min-height: 0; height: 5px; padding: 0; border-radius: 0 0 2px 2px;
  }
  .btn[data-edge="bottom"].oc-min { border-radius: 2px 2px 0 0; }
  .btn.oc-min .oc-grip,
  .btn.oc-min .oc-lbl {
    display: none;
    visibility: hidden;
  }
  .btn.oc-fs-hidden { opacity: 0; transform: translateX(112%); pointer-events: none; }
  .btn[data-edge="left"].oc-fs-hidden { transform: translateX(-112%); }
  .btn[data-edge="top"].oc-fs-hidden { transform: translateY(-112%); }
  .btn[data-edge="bottom"].oc-fs-hidden { transform: translateY(112%); }
  .btn.oc-fs-reveal { opacity: 1; transform: translateX(0); pointer-events: auto; }
  .panel {
    position: fixed;
    bottom: 80px;
    right: 20px;
    width: 360px;
    max-width: calc(100vw - 40px);
    max-height: calc(100vh - 120px);
    background: var(--oncatch-bg);
    color: var(--oncatch-fg);
    border: 1px solid var(--oncatch-border);
    border-radius: 12px;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    z-index: 2147483647;
    display: flex;
    flex-direction: column;
    font-size: 14px;
  }
  .panel[hidden] { display: none; }
  button, input, textarea, select { font-family: inherit; }
  .reporter-preferences { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 12px; margin: -4px 0 12px; }
  .reporter-preferences .field-checkbox { margin: 0; }
  .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 18px 18px 12px;
    border-bottom: 1px solid var(--oncatch-border);
    position: relative;
    cursor: grab;
    touch-action: none;
  }
  /* #501 — drag-handle pill at the top of the header; grab the header to move
     the whole panel anywhere in the viewport. */
  .panel-header::before {
    content: '';
    position: absolute;
    top: 6px;
    left: 50%;
    transform: translateX(-50%);
    width: 26px;
    height: 4px;
    border-radius: 2px;
    background: var(--oncatch-border);
    opacity: 0.55;
  }
  .panel.oc-panel-dragging { transition: none; }
  .panel.oc-panel-dragging .panel-header { cursor: grabbing; }
  /* #55 a11y: panel-title is an h2 element so SC 1.3.1 + 2.4.6 see real
     heading structure, not a styled div. Reset the browser's h2 defaults
     (large size + vertical margins) since the visual style here is
     intentional. */
  .panel-title { font-weight: 600; font-size: 15px; margin: 0; line-height: 1.2; }
  .panel-close {
    background: none;
    border: none;
    color: var(--oncatch-muted);
    cursor: pointer;
    font-size: 20px;
    line-height: 1;
    padding: 4px 8px;
    border-radius: 4px;
  }
  .panel-close:hover { background: #f3f4f6; }
  .panel-body {
    padding: 16px 18px;
    overflow: auto;
  }
  /* #55 a11y: each form field is itself a label element wrapping its input
     so SC 1.3.1 (Info-and-Relationships) passes without per-field IDs.
     .field is the label (block-display); .field-label-text is the visible
     label string above the input. */
  .field { display: block; margin-bottom: 12px; }
  .field-label-text {
    display: block;
    font-weight: 500;
    margin-bottom: 4px;
    font-size: 12px;
    color: var(--oncatch-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .field-required {
    color: var(--oncatch-accent);
  }
  /* Opt-in contact-prefill checkbox. Inline row (not the stacked .field
     layout) because the label reads as a sentence beside the box rather
     than as a caption above an input. Label wraps the input, same a11y
     pattern as .field, so no id/for pairing is needed. */
  .field-checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: -4px 0 12px;
    cursor: pointer;
  }
  .field-checkbox input {
    width: auto;
    margin: 0;
    cursor: pointer;
    flex: none;
  }
  /* #648: an author display declaration outranks the browser's built-in
     [hidden] { display: none } rule, so the display:flex on .field-checkbox
     above would keep the notify-on-fix checkbox VISIBLE even while the
     element carries the hidden attribute. Every hideable element in this
     stylesheet therefore needs its own targeted opt-out — see .panel[hidden],
     .att-record-unsupported[hidden] and .att-dropzone-error[hidden] — and
     this is that rule for the notify field. Scoped to .notify-fix-field on
     purpose: the identical latent problem on .reporter-name-field,
     .reporter-email-field and .reporter-save-contact-field belongs to #212
     and is filed separately rather than changing that feature's behavior
     inside this PR. (NOTE: this comment must stay backtick-free — the whole
     stylesheet lives in a JS template literal.) */
  .notify-fix-field[hidden] { display: none; }
  .field-checkbox-text {
    font-weight: 400;
    font-size: 12px;
    color: var(--oncatch-muted);
  }
  /* Surfaced when the browser blocks storage, so the opt-in never silently
     fails to save. Sits directly under the checkbox it explains. */
  .field-checkbox-notice {
    font-size: 12px;
    color: var(--oncatch-accent);
    margin: -8px 0 12px;
  }
  .field input, .field textarea {
    width: 100%;
    padding: 8px 10px;
    font-family: inherit;
    font-size: 14px;
    border: 1px solid var(--oncatch-border);
    border-radius: 6px;
    box-sizing: border-box;
    color: var(--oncatch-fg);
    background: white;
  }
  .field input:focus, .field textarea:focus {
    outline: 2px solid var(--oncatch-accent);
    outline-offset: -1px;
  }
  /* Required-field validation highlight — applied on submit when empty,
     cleared on first input event. Re-uses the accent rust so the cue
     reads as part of the existing palette. */
  .field input.field-invalid,
  .field textarea.field-invalid {
    border-color: var(--oncatch-accent);
    box-shadow: 0 0 0 1px var(--oncatch-accent);
  }
  .field textarea { min-height: 90px; resize: vertical; }
  .dictation-input { position:relative; }
  .dictation-input input { padding-right:44px; }
  .dictation-input textarea { display:block; padding-right:48px; }
  .dictation-speak { position:absolute; right:3px; top:50%; transform:translateY(-50%); display:flex; align-items:center; justify-content:center; width:32px; height:32px; padding:0; border:0; border-radius:4px; background:transparent; color:var(--oncatch-accent); cursor:pointer; }
  .dictation-input-multiline .dictation-speak { top:8px; right:8px; transform:none; }
  .dictation-speak:hover { background:var(--oncatch-bg); }
  .dictation-speak:focus-visible { outline:2px solid var(--oncatch-accent); outline-offset:-2px; }
  .dictation-speak svg { width:16px; height:16px; flex:none; }
  .dictation-waveform { position:absolute; right:40px; top:50%; transform:translateY(-50%); width:36px; height:18px; fill:var(--oncatch-accent); pointer-events:none; }
  .dictation-input-multiline .dictation-waveform { top:15px; right:48px; transform:none; }
  .dictation-waveform[hidden], .dictation-status[hidden] { display:none; }
  .dictation-active input { padding-right:84px; }
  .dictation-active textarea { padding-right:92px; }
  .dictation-speak[data-dictation-state="processing"] svg { animation:oncatch-dictation-spin 0.8s linear infinite; }
  @keyframes oncatch-dictation-spin { to { transform:rotate(360deg); } }
  .dictation-status.dictation-status-quiet { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip-path:inset(50%); white-space:nowrap; border:0; }
  @media (prefers-reduced-motion:reduce) { .dictation-speak[data-dictation-state="processing"] svg { animation:none; } }
  .dictation-status { display:block; margin-top:4px; font-size:12px; color:var(--oncatch-muted); }
  .dictation-speak:disabled { opacity:.5; cursor:default; }
  @media (pointer:coarse) {
    .dictation-speak { width:44px; height:44px; }
    .dictation-input input { min-height:50px; padding-right:54px; }
    .dictation-input textarea { padding-right:60px; }
    .dictation-waveform { right:52px; }
    .dictation-input-multiline .dictation-waveform { right:60px; top:21px; }
    .dictation-active input { padding-right:96px; }
    .dictation-active textarea { padding-right:104px; }
  }
  .submit {
    width: 100%;
    padding: 10px;
    background: var(--oncatch-accent);
    color: white;
    border: none;
    border-radius: 6px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    margin-top: 4px;
  }
  .submit:disabled { background: #9ca3af; cursor: default; }
  .submit-secondary {
    padding: 10px;
    background: white;
    color: var(--oncatch-fg);
    border: 1px solid var(--oncatch-border);
    border-radius: 6px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
  }
  .submit-secondary:hover { background: #f9fafb; }
  .action-row {
    display: flex;
    gap: 8px;
    margin-top: 4px;
  }
  .action-row .submit, .action-row .submit-secondary {
    flex: 1;
    margin-top: 0;
  }
  .success {
    padding: 24px 18px;
    text-align: center;
  }
  /* #55 a11y: success heading is h2 (matches panel dialog level). The
     :focus state has no outline because the heading carries tabindex=-1
     for programmatic focus only — the visible affordance is the localised
     copy + the rust-coloured ref code that follows. */
  .success-heading { color: var(--oncatch-accent); margin: 0 0 8px; font-size: 18px; font-weight: 600; line-height: 1.2; }
  .success-heading:focus { outline: none; }
  /* #256 partial-success notice when reconcile drops attachments — amber
     bordered block above the reference code with its own readable contrast.
     #55 contrast fix: border darkened from #fdba74 (1.59:1 / 1.69:1 — failed
     UI-component threshold) to #c2740c (3.41:1 / 3.62:1 — clears 3:1 with
     margin). Text remains #9a3412 on #fff7ed (6.88:1 — already passing). */
  .success .partial-notice {
    background: #fff7ed;
    border: 1px solid #c2740c;
    color: #9a3412;
    border-radius: 4px;
    padding: 10px 12px;
    margin: 0 0 12px;
    font-size: 13px;
    line-height: 1.4;
    text-align: left;
  }
  .success .ref {
    display: inline-block;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-weight: 600;
    background: var(--oncatch-accent-soft);
    color: var(--oncatch-accent);
    padding: 4px 10px;
    border-radius: 4px;
    margin: 8px 0 16px;
  }
  .error {
    color: #b91c1c;
    font-size: 13px;
    margin-top: 8px;
  }
  .oc-retry {
    appearance: none;
    margin-top: 12px;
    border: 1px solid var(--oncatch-border);
    border-radius: 5px;
    background: #fff;
    color: var(--oncatch-accent);
    cursor: pointer;
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    padding: 7px 10px;
  }
  .oc-retry:hover { background: var(--oncatch-accent-soft); }
  .oc-retry:focus-visible { outline: 3px solid var(--oncatch-accent); outline-offset: 2px; }
  /* #598 — shown between the reporter's first click and the config landing. */
  .oc-pending {
    color: var(--oncatch-muted);
    font-size: 13px;
    padding: 18px 0;
    text-align: center;
  }
  .footer {
    padding: 10px 18px;
    border-top: 1px solid var(--oncatch-border);
    font-size: 11px;
    color: var(--oncatch-muted);
    text-align: center;
  }
  .footer a { color: var(--oncatch-muted); text-decoration: none; }
  .challenge-copy { margin-bottom: 16px; }
  .challenge-heading { font-size: 14px; font-weight: 500; color: var(--oncatch-fg); margin-bottom: 4px; }
  .challenge-sub { font-size: 12px; color: var(--oncatch-muted); }
  .challenge-host { min-height: 70px; display: flex; justify-content: center; align-items: center; }

  /* #161: attachments */
  .attachments { margin-bottom: 12px; position: relative; }
  .attachments-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
  }
  .attachments-label {
    font-weight: 500;
    font-size: 12px;
    color: var(--oncatch-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .attachments-count {
    font-size: 11px;
    color: var(--oncatch-muted);
  }
  .attachments-controls {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
    margin-bottom: 8px;
  }
  .attach-btn {
    padding: 8px 6px;
    background: white;
    color: var(--oncatch-fg);
    border: 1px dashed var(--oncatch-border);
    border-radius: 6px;
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }
  .attach-btn:hover:not(:disabled) {
    background: #f9fafb;
    border-color: var(--oncatch-fg);
  }
  .attach-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .attach-btn svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
  /* #136: cap-full degradation notice. Same vertical footprint as the
     .attachments-controls grid so the form layout doesn't shift between
     normal and full states. */
  .att-storage-full {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    background: var(--oncatch-accent-soft);
    color: #6b3a1f;
    border: 1px solid #f1c1a3;
    border-radius: 6px;
    font-size: 12px;
    line-height: 1.4;
    margin-bottom: 8px;
  }
  .att-storage-full svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: var(--oncatch-accent);
  }
  /* #163 PR C — banner shown when the browser doesn't support screen
     recording (Safari iOS 17.0-17.3, older browsers). Lower-key visual
     than the storage-full notice — this is an informational status, not
     an actionable error. */
  .att-record-unsupported {
    padding: 8px 12px;
    background: #f5f3ee;
    color: var(--oncatch-muted);
    border: 1px dashed var(--oncatch-border);
    border-radius: 6px;
    font-size: 12px;
    line-height: 1.4;
    margin-bottom: 8px;
  }
  .att-record-unsupported[hidden] {
    display: none;
  }
  /* #605: upload-your-own-screenshot dropzone. Compact vertical box for the
     360px panel: icon, explicit picker button, one hint line. Whole container
     is the drop target; .is-dragover marks an active file drag. */
  .att-dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    padding: 10px 12px;
    margin-bottom: 8px;
    border: 1px dashed var(--oncatch-border);
    border-radius: 6px;
    background: white;
    text-align: center;
    transition: border-color 0.15s ease, background 0.15s ease;
  }
  .att-dropzone svg {
    width: 20px;
    height: 20px;
    color: var(--oncatch-muted);
    flex-shrink: 0;
  }
  .att-dropzone.is-dragover {
    border-color: var(--oncatch-accent);
    background: var(--oncatch-accent-soft);
  }
  .att-dropzone-btn {
    padding: 6px 14px;
    background: var(--oncatch-accent);
    color: white;
    border: none;
    border-radius: 6px;
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
  }
  .att-dropzone-btn:hover:not(:disabled) {
    background: #a83a0b;
  }
  .att-dropzone-btn:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
  .att-dropzone-hint[hidden] { display: none; }
  .att-dropzone-hint {
    font-size: 11px;
    color: var(--oncatch-muted);
    line-height: 1.35;
  }
  .att-dropzone-error {
    font-size: 11px;
    color: #b91c1c;
    line-height: 1.35;
  }
  .att-dropzone-error[hidden] {
    display: none;
  }
  .attachments-slots {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 6px;
    min-height: 0;
  }
  .attachments-slots:empty {
    display: none;
  }
  .att-slot {
    aspect-ratio: 1;
    position: relative;
    background: #f3f4f6;
    border: 1px solid var(--oncatch-border);
    border-radius: 6px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .att-slot-file-name { position:absolute; left:4px; right:4px; bottom:4px; padding:2px; font-size:10px; overflow-wrap:anywhere; background:white; color:var(--oncatch-fg); }
  .att-slot.is-failed { border-color: #b91c1c; background: #fef2f2; }
  .att-slot-thumb {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .att-slot-icon {
    width: 24px;
    height: 24px;
    color: var(--oncatch-muted);
  }
  .att-slot-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  /* #536 — non-covering progress badge for slots whose thumbnail is ready.
     Small disc in the bottom-left corner (bottom-right is the Remove button's
     side of the tile at the top, but keep clear of it) so the preview stays
     fully visible while the upload runs. */
  .att-slot-badge {
    position: absolute;
    bottom: 2px;
    left: 2px;
    max-width: calc(100% - 4px);
    min-height: 22px;
    padding: 0 4px;
    gap: 4px;
    border-radius: 4px;
    color: white;
    font-size: 9px;
    background: rgba(0, 0, 0, 0.65);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .att-slot-spinner {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: oncatch-spin 0.7s linear infinite;
  }
  .att-slot-badge .att-slot-spinner {
    width: 12px;
    height: 12px;
  }
  @keyframes oncatch-spin {
    to { transform: rotate(360deg); }
  }
  /* #533 — animated indicator on the submit button. The button previously only
     swapped its label, which reads as "nothing happened" on a slow network. */
  .submit-spinner {
    display: inline-block;
    width: 13px;
    height: 13px;
    margin-right: 7px;
    vertical-align: -2px;
    border: 2px solid rgba(255, 255, 255, 0.35);
    border-top-color: currentColor;
    border-radius: 50%;
    animation: oncatch-spin 0.7s linear infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    .att-slot-spinner, .submit-spinner { animation-duration: 2s; }
  }
  .att-slot-overlay[data-att-slot-progress] {
    flex-direction: column;
    gap: 4px;
    padding: 0 6px;
  }
  .att-slot-progress-pct {
    font-size: 10px;
    font-weight: 600;
    color: white;
    font-variant-numeric: tabular-nums;
  }
  .att-slot-progress-track {
    width: 100%;
    height: 3px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.3);
    overflow: hidden;
  }
  .att-slot-progress-fill {
    height: 100%;
    background: white;
    border-radius: 2px;
    transition: width 150ms ease;
  }
  .att-slot-action {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    border: none;
    cursor: pointer;
    font-size: 11px;
    line-height: 1;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .att-slot-action:hover { background: rgba(0, 0, 0, 0.85); }
  .att-slot-error-msg {
    position: absolute;
    inset: auto 0 0 0;
    background: #b91c1c;
    color: white;
    padding: 2px 4px;
    font-size: 9px;
    text-align: center;
    line-height: 1.2;
  }
  .att-slot-retry {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: white;
    color: var(--oncatch-fg);
    border: 1px solid var(--oncatch-border);
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-family: inherit;
    cursor: pointer;
  }
  .att-slot-retry:hover { background: #f3f4f6; }
  /* #161: blocker modal */
  .modal {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.96);
    z-index: 20;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 18px;
    text-align: center;
  }
  .modal h3 { margin: 0 0 8px; font-size: 15px; color: var(--oncatch-fg); }
  .modal p {
    margin: 0 0 16px;
    color: var(--oncatch-muted);
    font-size: 13px;
    line-height: 1.5;
  }
  .modal-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }
`;
}

export interface MountedUi {
  open: () => void;
  close: () => void;
  root: ShadowRoot;
  destroy: () => void;
  /**
   * Show Turnstile challenge inside the panel. Resolves with the token on pass.
   * Rejects on cancel, timeout, or load failure — caller shows an error.
   * The implementation lazy-loads the challenge module on first call.
   */
  showChallenge: (input: ChallengeInput) => Promise<string>;
  /**
   * #598 — merge late-arriving config into the live handlers object and
   * re-render the form so it picks the new values up. `renderForm` reads every
   * config-derived field (storageState, sensitiveDataMode, privacyConfig,
   * messages, workspaceName, attachmentRetentionDays) off `handlers` at render
   * time, so a merge + re-render is all that's required.
   *
   * Used by the deferred-config path and by the #503 background revalidation,
   * which previously could only destroy the widget, never correct it.
   */
  applyConfig: (patch: Partial<UiHandlers>) => void;
}

export interface ChallengeInput {
  siteKey: string;
  action: string;
  cdata: string;
}

/**
 * #477: apply the widget's CSS inside the shadow root WITHOUT an injected
 * <style> element. A raw `<style>` is governed by the host page's CSP
 * `style-src-elem`: a site running a strict nonce policy (e.g. Next.js
 * `style-src-elem 'self' 'nonce-…'` with no 'unsafe-inline') blocks the
 * un-nonced element, so the widget mounts completely unstyled and the launcher
 * renders invisible. The widget can't read the host's per-request nonce, so a
 * <style> tag can never satisfy such a policy. Constructable stylesheets are
 * applied programmatically and are NOT subject to `style-src`, so they render
 * under any strict CSP. Falls back to a <style> element on engines without
 * constructable-stylesheet support (which predate strict-CSP adoption, so the
 * block does not apply there anyway).
 *
 * Exported for the #477 regression test.
 */
export function applyShadowStyles(shadow: ShadowRoot, css: string): void {
  try {
    if (typeof CSSStyleSheet === 'function' && 'replaceSync' in CSSStyleSheet.prototype) {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(css);
      shadow.adoptedStyleSheets = [sheet];
      return;
    }
  } catch {
    // Constructor unavailable / threw → fall through to the <style> element.
  }
  const style = document.createElement('style');
  style.textContent = css;
  shadow.appendChild(style);
}

export function mountUi(handlers: UiHandlers): MountedUi {
  const host = document.createElement('div');
  host.id = 'oncatch-widget-host';
  host.style.cssText = 'all: initial;';
  // #56: set lang on the shadow host so screen readers + browser features
  // (text-to-speech, hyphenation, content-visibility heuristics) see the
  // correct reporter language. The shadow root inherits this via the host
  // boundary in modern engines (Chromium, Gecko, WebKit).
  host.setAttribute('lang', handlers.locale);
  document.body.appendChild(host);

  const shadow = host.attachShadow({ mode: 'closed' });

  // #326: isolate keystrokes inside the widget from host-page listeners.
  // Without this, a host page that wires global shortcuts (e.g.,
  // `document.addEventListener('keydown', ...)` for an audio-player spacebar
  // play/pause) hijacks every space, arrow, and modifier combo typed into
  // the widget's inputs. The fix attaches stopPropagation to every key event
  // bubbling through the shadow root — events still reach widget-internal
  // listeners attached on the shadow tree, but never cross the shadow
  // boundary into the host document.
  //
  // Browser-level shortcuts (Cmd+R, F5, Cmd+T) are unaffected because the
  // browser handles them before any JS listener runs. Host-page shortcuts
  // fired while the user has clicked back onto the host page (focus outside
  // the widget) are also unaffected, because those events do not originate
  // inside this shadow root.
  //
  // Known limit: a host-page listener registered with `{ capture: true }` at
  // `document` or above, BEFORE the widget script loaded, will see the event
  // during the capture phase — earlier in the propagation chain than the
  // shadow root. That race is irreducible without an iframe rewrite (out of
  // scope here) and is not the audio-player class of bug.
  const stopKey = (event: Event) => event.stopPropagation();
  shadow.addEventListener('keydown', stopKey);
  shadow.addEventListener('keyup', stopKey);
  shadow.addEventListener('keypress', stopKey);

  // #477: apply styles in a way a strict host CSP cannot block (see
  // applyShadowStyles). A raw <style> here is blocked by a host's
  // `style-src-elem 'self' 'nonce-…'` policy, leaving the launcher invisible.
  applyShadowStyles(shadow, buildStyles());

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'btn';
  button.setAttribute('aria-label', handlers.messages.panelTitle);
  // Toggle semantics for assistive tech — flipped in sync with panel.hidden
  // on every click (and on the in-panel [×] close).
  button.setAttribute('aria-expanded', 'false');
  button.setAttribute('aria-haspopup', 'dialog');
  // DOM-built so the side grip survives strict Trusted Types.
  const ribbonGrip = document.createElement('span');
  ribbonGrip.className = 'oc-grip';
  ribbonGrip.setAttribute('aria-hidden', 'true');
  const ribbonDots = document.createElement('span');
  ribbonDots.className = 'oc-dots';
  for (let i = 0; i < 6; i += 1) ribbonDots.appendChild(document.createElement('i'));
  ribbonGrip.appendChild(ribbonDots);
  button.appendChild(ribbonGrip);
  const ribbonLabel = document.createElement('span');
  ribbonLabel.className = 'oc-lbl';
  ribbonLabel.textContent = 'Feedback';
  button.appendChild(ribbonLabel);
  // Custom-launcher mode: the host page supplies its own launcher (e.g. an edge
  // ribbon) and drives the widget via open()/close(). Keep the button in the DOM
  // (display:none) rather than skipping it so internal refs + aria wiring stay
  // valid; it's simply never shown or interactive.
  if (handlers.hideLauncher) button.style.display = 'none';
  shadow.appendChild(button);

  const panel = document.createElement('div');
  panel.className = 'panel';
  panel.hidden = true;
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'false');
  panel.setAttribute('aria-labelledby', 'oncatch-panel-title');
  shadow.appendChild(panel);
  shadow.addEventListener(PANEL_CLOSE_EVENT, () => assistant?.cancel());

  // #501 — drag the panel by its header to reposition it anywhere. Switches the
  // panel from bottom/right anchoring to free left/top, clamps it on-screen, and
  // persists the spot across open/close. Independent of launcher mode.
  const PANEL_POS_KEY = 'oncatch_panel_pos';
  const readPanelPos = (): { left: number; top: number } | null => {
    try {
      const v = JSON.parse(localStorage.getItem(PANEL_POS_KEY) ?? 'null');
      return v && typeof v.left === 'number' && typeof v.top === 'number' ? v : null;
    } catch {
      return null;
    }
  };
  const applyPanelPos = (left: number, top: number): void => {
    const w = panel.offsetWidth || 360;
    const h = panel.offsetHeight || 400;
    const maxLeft = Math.max(8, window.innerWidth - w - 8);
    const maxTop = Math.max(8, window.innerHeight - h - 8);
    panel.style.left = `${Math.min(Math.max(8, left), maxLeft)}px`;
    panel.style.top = `${Math.min(Math.max(8, top), maxTop)}px`;
    panel.style.right = 'auto';
    panel.style.bottom = 'auto';
  };
  const restorePanelPos = (): void => {
    const p = readPanelPos();
    if (p) applyPanelPos(p.left, p.top);
  };
  let panelDragging = false;
  let panelStartX = 0;
  let panelStartY = 0;
  let panelBaseLeft = 0;
  let panelBaseTop = 0;
  panel.addEventListener('pointerdown', (e) => {
    const target = e.target as Element;
    // Only the header is the drag handle; the close button stays a button.
    if (!target.closest('.panel-header') || target.closest('.panel-close, .oc-assistant-toggle'))
      return;
    panelDragging = true;
    const rect = panel.getBoundingClientRect();
    panelBaseLeft = rect.left;
    panelBaseTop = rect.top;
    panelStartX = e.clientX;
    panelStartY = e.clientY;
    panel.classList.add('oc-panel-dragging');
    try {
      panel.setPointerCapture(e.pointerId);
    } catch {
      /* pointer capture is best-effort */
    }
    e.preventDefault();
  });
  panel.addEventListener('pointermove', (e) => {
    if (!panelDragging) return;
    applyPanelPos(
      panelBaseLeft + (e.clientX - panelStartX),
      panelBaseTop + (e.clientY - panelStartY),
    );
  });
  const endPanelDrag = (e: PointerEvent): void => {
    if (!panelDragging) return;
    panelDragging = false;
    panel.classList.remove('oc-panel-dragging');
    try {
      panel.releasePointerCapture(e.pointerId);
    } catch {
      /* pointer capture is best-effort */
    }
    const rect = panel.getBoundingClientRect();
    try {
      localStorage.setItem(PANEL_POS_KEY, JSON.stringify({ left: rect.left, top: rect.top }));
    } catch {
      /* storage unavailable — position just won't persist */
    }
  };
  panel.addEventListener('pointerup', endPanelDrag);
  panel.addEventListener('pointercancel', endPanelDrag);

  // Track whether the panel is currently showing the post-submit success screen.
  // When the user re-opens the widget after viewing success, we want a fresh form
  // — not a stale "Thanks, we got it" for a bug they already filed.
  let showingSuccess = false;

  let setRibbonMinimized = (_value: boolean): void => {
    // Bound after launcher state initializes; custom launchers have no setting.
  };
  function addRibbonSetting(): void {
    if (handlers.hideLauncher) return;
    const body = panel.querySelector('.reporter-preferences');
    if (!body) return;
    const label = document.createElement('label');
    label.className = 'field-checkbox ribbon-setting';

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.name = 'minimize-ribbon';
    input.checked = button.classList.contains('oc-remember-min');
    input.addEventListener('change', () => setRibbonMinimized(input.checked));
    const text = document.createElement('span');
    text.className = 'field-checkbox-text';
    text.textContent = 'Minimize ribbon';
    label.append(input, text);
    body.appendChild(label);
  }

  let assistant: ReturnType<typeof mountAssistant> | undefined;
  function resetToForm() {
    assistant?.destroy();
    // #215 — fresh form means fresh pin state. If a previous flow captured a
    // pin and then bailed (closed widget, switched to a non-pin capture,
    // removed the screenshot attachment), don't carry the stale pin data
    // into the next submission.
    activePin = null;
    renderForm(panel, handlers, {
      onAfterRender: () => {
        showingSuccess = false;
        addRibbonSetting();
        assistant = mountAssistant(panel, handlers.assistantEndpoint);
      },
    });
  }

  function showSuccess(referenceCode: string, partial?: { failed: number; total: number }) {
    // A receipt sizes to its contents even after a reporter resized the form.
    panel.style.height = '';
    panel.style.minHeight = '';
    renderSuccess(panel, referenceCode, handlers, resetToForm, partial);
    showingSuccess = true;
  }

  // #598 — deferred-config mode. `deferred` stays true only until the first
  // open resolves; every open path funnels through ensureReady() below.
  const deferred = Boolean(handlers.deferInitialRender && handlers.onFirstOpen);
  let firstOpenStarted = false;
  let ready = !deferred;

  /**
   * #598 — panel body shown between the reporter's first click and the config
   * landing. Deliberately minimal: a title and a status line, no form, so we
   * never render inputs whose behaviour (attachments, recording, masking)
   * isn't decided yet. `role=status` + `aria-live` announce the transition.
   */
  function renderPending(): void {
    setTrustedHtml(
      panel,
      `
      <div class="panel-header">
        <h2 class="panel-title" id="oncatch-panel-title">${escapeHtml(handlers.messages.panelTitle)}</h2>
        <button type="button" class="panel-close" aria-label="${escapeAttr(handlers.messages.panelClose)}">×</button>
      </div>
      <div class="panel-body">
        <div class="oc-pending" role="status" aria-live="polite">Loading…</div>
      </div>
      <div class="footer">Powered by OnCatch</div>
    `,
    );
    panel.querySelector<HTMLButtonElement>('.panel-close')?.addEventListener('click', () => {
      panel.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      shadow.dispatchEvent(new CustomEvent(PANEL_CLOSE_EVENT));
      handlers.onClose();
    });
  }

  /**
   * #598 — terminal state when the widget cannot run on this origin.
   *
   * Pre-#598 this case was invisible: bootstrap logged a console warning and
   * no widget ever appeared, so a customer who mis-registered their origin got
   * silence. Deferring the config fetch means the launcher is already on screen
   * by the time we find out, so we say what happened instead. That is a net
   * improvement in diagnosability, and it only ever renders for a workspace
   * that is genuinely disabled, rate limited, or not allowlisted.
   */
  function renderUnavailable(reason: 'disabled' | 'offline'): void {
    const copy =
      reason === 'offline'
        ? 'We couldn’t reach the feedback service. Check your connection and try again.'
        : 'Feedback isn’t available on this site right now.';
    setTrustedHtml(
      panel,
      `
      <div class="panel-header">
        <h2 class="panel-title" id="oncatch-panel-title">${escapeHtml(handlers.messages.panelTitle)}</h2>
        <button type="button" class="panel-close" aria-label="${escapeAttr(handlers.messages.panelClose)}">×</button>
      </div>
      <div class="panel-body">
        <div class="error" role="alert">${escapeHtml(copy)}</div>
        <button type="button" class="oc-retry">Try again</button>
      </div>
      <div class="footer">Powered by OnCatch</div>
    `,
    );
    panel.querySelector<HTMLButtonElement>('.panel-close')?.addEventListener('click', () => {
      panel.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      shadow.dispatchEvent(new CustomEvent(PANEL_CLOSE_EVENT));
      handlers.onClose();
    });
    // #703 — the endpoint deliberately does not reveal whether this was a
    // disabled domain, origin mismatch, rate-limit, or unknown workspace. A
    // reporter can still retry a transient failure, but only by an explicit
    // activation: never spin in the background and make an outage worse.
    panel.querySelector<HTMLButtonElement>('.oc-retry')?.addEventListener('click', () => {
      void ensureReady().then(() => {
        if (!panel.hidden) {
          shadow.querySelector<HTMLInputElement>('input[name=title]')?.focus();
        }
      });
    });
  }

  function applyConfig(patch: Partial<UiHandlers>): void {
    // Reauthorization cancels draft audio, but must not erase manually typed text.
    const preserveText = dictationForms.has(panel)
      ? Array.from(
          panel.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
            'input[name="title"], textarea[name="summary"]',
          ),
        ).map((field) => ({ name: field.name, value: field.value }))
      : [];
    Object.assign(handlers, patch);
    // Only re-render once the form is the thing on screen. Re-rendering while
    // the panel shows success or a challenge would wipe the reporter's
    // reference code / in-flight verification out from under them.
    if (ready && !showingSuccess) {
      resetToForm();
      for (const saved of preserveText) {
        const field = panel.querySelector<HTMLInputElement | HTMLTextAreaElement>(
          `[name="${saved.name}"]`,
        );
        if (field) field.value = saved.value;
      }
    }
  }

  /**
   * #598 — resolve config on the first open, at most once. Concurrent opens
   * (double-click the launcher) share the same in-flight promise via
   * `firstOpenStarted`; the panel is already showing the pending state so a
   * second click is a no-op rather than a second /widget-config round trip.
   */
  async function ensureReady(): Promise<void> {
    if (ready || firstOpenStarted || !handlers.onFirstOpen) return;
    firstOpenStarted = true;
    renderPending();
    let outcome: Awaited<ReturnType<NonNullable<UiHandlers['onFirstOpen']>>>;
    try {
      outcome = await handlers.onFirstOpen();
    } catch {
      // onFirstOpen is bootstrap-owned and already guards its own failures;
      // this is belt-and-braces so a throw can never strand the panel on the
      // pending state with no way forward.
      outcome = { ok: false, reason: 'offline' };
    }
    if (!outcome.ok) {
      // Leave `ready` false so a later open retries — an 'offline' result is
      // frequently transient and the reporter's obvious next move is to click
      // again. `firstOpenStarted` resets for the same reason.
      firstOpenStarted = false;
      renderUnavailable(outcome.reason);
      return;
    }
    if (outcome.patch) Object.assign(handlers, outcome.patch);
    ready = true;
    resetToForm();
  }

  if (deferred) {
    // Nothing rendered into the panel until the first open. The launcher is
    // already in the shadow root above, so the widget is visible and clickable
    // without a single network request having been made.
    renderPending();
  } else {
    resetToForm();
  }

  // #497 — edge-ribbon interaction. Skipped in custom-launcher mode
  // (hideLauncher), where the host supplies its own launcher and drives the
  // widget via the public open()/close() API.
  let releaseLauncherChrome = (): void => {
    // Replaced once the ribbon mounts its resize listener.
  };
  if (!handlers.hideLauncher) {
    // Toggle: open if closed, close if open. Preserves the prior corner-button
    // behaviour — a11y aria-expanded, capture-flow abort via PANEL_CLOSE_EVENT,
    // focus first field on open — now reached by a click/tap that ISN'T a drag,
    // or by Enter/Space.
    const togglePanel = (): void => {
      if (!panel.hidden) {
        panel.hidden = true;
        button.setAttribute('aria-expanded', 'false');
        // #416 — close/reopen on the same page preserves all state (text, pin,
        // attachments). PANEL_CLOSE_EVENT still fires so in-progress capture
        // flows (annotation, region select) abort; completed captures persist.
        shadow.dispatchEvent(new CustomEvent(PANEL_CLOSE_EVENT));
        handlers.onClose();
        return;
      }
      if (ready && showingSuccess) {
        resetToForm();
      }
      panel.hidden = false;
      restorePanelPos();
      button.setAttribute('aria-expanded', 'true');
      handlers.onOpen();
      // #598 — in deferred mode the form doesn't exist yet; ensureReady renders
      // it once config lands, then focuses. Resolves immediately when config is
      // already in hand, so the non-deferred path is unchanged.
      void ensureReady().then(() => {
        if (!panel.hidden) {
          shadow.querySelector<HTMLInputElement>('input[name=title]')?.focus();
        }
      });
    };

    const POS_KEY = 'oncatch_launcher_dock';
    const LEGACY_POS_KEY = 'oncatch_launcher_top';
    const MIN_KEY = 'oncatch_launcher_minimized';
    const viewport = () => ({
      width: document.documentElement.clientWidth || window.innerWidth,
      height: document.documentElement.clientHeight || window.innerHeight,
    });
    let dock: RibbonDock = { edge: 'right', offset: viewport().height * 0.4 };
    let rememberedMin = false;
    try {
      const saved = parseRibbonDock(localStorage.getItem(POS_KEY));
      const legacy = Number.parseFloat(localStorage.getItem(LEGACY_POS_KEY) ?? '');
      dock =
        saved ??
        (Number.isFinite(legacy) && legacy >= 0 ? { edge: 'right', offset: legacy } : dock);
      rememberedMin = localStorage.getItem(MIN_KEY) === '1';
    } catch {
      /* Restricted storage does not prevent docking. */
    }
    let pointerInside = false;
    let activePointer: number | null = null;
    let moved = false;
    let suppressClick = false;
    let clickReset: ReturnType<typeof setTimeout> | undefined;
    let previousDock = { ...dock };
    let start = { x: 0, y: 0 };
    let anchor = { x: 0, y: 0 };
    let dragBox = { width: 42, height: 112 };
    let previewEdge: RibbonDock['edge'] | null = null;
    const DRAG_THRESHOLD = 5;
    const size = () => {
      const horizontal = dock.edge === 'top' || dock.edge === 'bottom';
      const minimized = button.classList.contains('oc-min');
      const view = viewport();
      return {
        width: Math.min(view.width, horizontal ? 112 : minimized ? 5 : 30),
        height: Math.min(view.height, horizontal ? (minimized ? 5 : 30) : 112),
      };
    };
    const edgeInsets = () => {
      const root = document.documentElement;
      const scroller = document.scrollingElement ?? root;
      let rtl = root.dir === 'rtl';
      try {
        rtl = getComputedStyle(root).direction === 'rtl';
      } catch {
        /* Detached host fallback. */
      }
      const overlay = overlayScrollbarCoversEdge(
        navigator.platform ?? '',
        navigator.userAgent ?? '',
      );
      const metrics = {
        innerWidth: window.innerWidth,
        clientWidth: root.clientWidth,
        scrollHeight: scroller.scrollHeight,
        clientHeight: scroller.clientHeight,
        overlayCoversEdge: overlay,
        rtl,
      };
      return {
        right: launcherRightInset(metrics),
        left: rtl ? launcherRightInset({ ...metrics, rtl: false }) : 0,
        bottom:
          overlay &&
          window.innerHeight - root.clientHeight <= 1 &&
          scroller.scrollWidth > scroller.clientWidth + 1
            ? OVERLAY_SCROLLBAR_CLEARANCE
            : 0,
      };
    };
    const applyDock = () => {
      button.dataset.edge = dock.edge;
      const view = viewport();
      const box = size();
      const placement = ribbonPlacement(dock, view, box, edgeInsets());
      dock = { ...dock, offset: placement.offset };
      button.style.left = dock.edge === 'right' ? 'auto' : `${placement.x}px`;
      button.style.right =
        dock.edge === 'right' ? `${Math.max(0, view.width - box.width - placement.x)}px` : 'auto';
      button.style.top = dock.edge === 'bottom' ? 'auto' : `${placement.y}px`;
      button.style.bottom =
        dock.edge === 'bottom'
          ? `${Math.max(0, view.height - box.height - placement.y)}px`
          : 'auto';
    };
    const saveDock = () => {
      try {
        localStorage.setItem(POS_KEY, JSON.stringify(dock));
        if (dock.edge === 'right') localStorage.setItem(LEGACY_POS_KEY, String(dock.offset));
      } catch {
        /* Docking remains usable without persistence. */
      }
    };
    const syncLauncherSize = () => {
      const peeking = rememberedMin && pointerInside;
      button.classList.toggle('oc-min', rememberedMin && !peeking);
      button.classList.toggle('oc-peek', peeking);
      button.classList.toggle('oc-remember-min', rememberedMin);
      const setting = panel.querySelector<HTMLInputElement>('input[name="minimize-ribbon"]');
      if (setting) setting.checked = rememberedMin;
      if (activePointer === null) applyDock();
    };
    setRibbonMinimized = (value) => {
      rememberedMin = value;
      try {
        if (value) localStorage.setItem(MIN_KEY, '1');
        else localStorage.removeItem(MIN_KEY);
      } catch {
        /* The size preference is still valid for this page. */
      }
      if (value) pointerInside = false;
      syncLauncherSize();
    };
    syncLauncherSize();
    button.addEventListener('pointerenter', () => {
      pointerInside = true;
      if (activePointer === null) syncLauncherSize();
    });
    button.addEventListener('pointerleave', () => {
      pointerInside = false;
      if (activePointer === null) syncLauncherSize();
    });
    button.addEventListener('click', () => {
      if (suppressClick) {
        suppressClick = false;
        return;
      }
      togglePanel();
    });
    button.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || !e.isPrimary || activePointer !== null) return;
      activePointer = e.pointerId;
      moved = false;
      suppressClick = false;
      previousDock = { ...dock };
      start = { x: e.clientX, y: e.clientY };
      const placement = ribbonPlacement(dock, viewport(), size(), edgeInsets());
      const box = size();
      dragBox = box;
      previewEdge = null;
      anchor = {
        x: Math.max(0, Math.min(1, (e.clientX - placement.x) / Math.max(1, box.width))),
        y: Math.max(0, Math.min(1, (e.clientY - placement.y) / Math.max(1, box.height))),
      };
      try {
        button.setPointerCapture(e.pointerId);
      } catch {
        /* Window listeners are the fallback. */
      }
    });
    const move = (e: PointerEvent) => {
      if (activePointer !== e.pointerId) return;
      const dx = e.clientX - start.x,
        dy = e.clientY - start.y;
      if (!moved && Math.hypot(dx, dy) <= DRAG_THRESHOLD) return;
      moved = true;
      button.classList.add('dragging');
      const view = viewport();
      // Use the grabbed ribbon's bounds, not just the pointer. Keep the
      // original dimensions for hit testing so rotating cannot move the zone.
      const probe = {
        x:
          e.clientX < view.width / 2
            ? e.clientX - anchor.x * dragBox.width
            : e.clientX + (1 - anchor.x) * dragBox.width,
        y:
          e.clientY < view.height / 2
            ? e.clientY - anchor.y * dragBox.height
            : e.clientY + (1 - anchor.y) * dragBox.height,
      };
      const movement = { x: dx, y: dy };
      previewEdge =
        chooseRibbonEdge(probe, view, previousDock.edge, movement, previousDock.edge) ??
        chooseRibbonEdge(probe, view, previousDock.edge, movement) ??
        previewEdge;
      dock.edge = previewEdge ?? previousDock.edge;
      button.dataset.edge = dock.edge;
      const box = size();
      button.style.right = 'auto';
      button.style.bottom = 'auto';
      button.style.left = `${Math.max(0, Math.min(e.clientX - anchor.x * box.width, view.width - box.width))}px`;
      button.style.top = `${Math.max(0, Math.min(e.clientY - anchor.y * box.height, view.height - box.height))}px`;
    };
    const finish = (e?: PointerEvent, cancelled = false) => {
      if (activePointer === null || (e && e.pointerId !== activePointer)) return;
      const pointer = activePointer;
      activePointer = null;
      if (moved) {
        // Commit exactly the target already shown during the drag.
        const edge = !cancelled && e ? previewEdge : null;
        dock = { ...previousDock };
        pointerInside = false;
        if (edge && e) {
          dock.edge = edge;
          button.dataset.edge = edge;
          syncLauncherSize();
          const box = size();
          dock.offset =
            edge === 'left' || edge === 'right'
              ? e.clientY - box.height / 2
              : e.clientX - box.width / 2;
        }
        suppressClick = true;
        clearTimeout(clickReset);
        clickReset = setTimeout(() => {
          suppressClick = false;
        }, 0);
      }
      button.classList.remove('dragging');
      syncLauncherSize();
      if (moved && !cancelled) saveDock();
      try {
        button.releasePointerCapture(pointer);
      } catch {
        /* Already released by the browser. */
      }
    };
    const up = (e: PointerEvent) => finish(e);
    const cancel = (e: PointerEvent) => finish(e, true);
    const blur = () => finish(undefined, true);
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', up, true);
    window.addEventListener('pointercancel', cancel, true);
    window.addEventListener('blur', blur);
    button.addEventListener('lostpointercapture', cancel);
    button.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') suppressClick = false;
      if (e.key === 'Escape') {
        finish(undefined, true);
        return;
      }
      const edges: Record<string, RibbonEdge> = {
        ArrowLeft: 'left',
        ArrowRight: 'right',
        ArrowUp: 'top',
        ArrowDown: 'bottom',
      };
      const edge = edges[e.key];
      if (!edge || activePointer !== null) return;
      const vertical = dock.edge === 'left' || dock.edge === 'right';
      if (e.altKey) {
        e.preventDefault();
        const oldBox = size();
        const old = ribbonPlacement(dock, viewport(), oldBox, edgeInsets());
        dock.edge = edge;
        button.dataset.edge = edge;
        const box = size();
        dock.offset =
          edge === 'left' || edge === 'right'
            ? old.y + oldBox.height / 2 - box.height / 2
            : old.x + oldBox.width / 2 - box.width / 2;
      } else if (
        (vertical && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) ||
        (!vertical && (e.key === 'ArrowLeft' || e.key === 'ArrowRight'))
      ) {
        e.preventDefault();
        dock.offset += e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -20 : 20;
      } else return;
      applyDock();
      saveDock();
    });
    const onResize = () => {
      finish(undefined, true);
      applyDock();
    };
    window.addEventListener('resize', onResize);
    let edgeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver === 'function') {
      edgeObserver = new ResizeObserver(() => {
        if (activePointer === null) applyDock();
      });
      try {
        edgeObserver.observe(document.documentElement);
      } catch {
        edgeObserver.disconnect();
        edgeObserver = null;
      }
    }
    let revealTimer: ReturnType<typeof setTimeout> | null = null;
    let fullscreenHost: Element | null = null;
    const fsElement = () =>
      document.fullscreenElement ??
      (document as unknown as { webkitFullscreenElement?: Element }).webkitFullscreenElement ??
      null;
    const onFsPointerMove = (e: PointerEvent) => {
      const view = viewport();
      const near =
        dock.edge === 'right'
          ? e.clientX > view.width - 80
          : dock.edge === 'left'
            ? e.clientX < 80
            : dock.edge === 'top'
              ? e.clientY < 80
              : e.clientY > view.height - 80;
      if (!near) return;
      button.classList.add('oc-fs-reveal');
      button.classList.remove('oc-fs-hidden');
      if (revealTimer) clearTimeout(revealTimer);
      revealTimer = setTimeout(() => {
        button.classList.add('oc-fs-hidden');
        button.classList.remove('oc-fs-reveal');
      }, 2500);
    };
    const onFsChange = () => {
      finish(undefined, true);
      fullscreenHost?.removeEventListener('pointermove', onFsPointerMove as EventListener);
      fullscreenHost = fsElement();
      if (revealTimer) clearTimeout(revealTimer);
      button.classList.toggle('oc-fs-hidden', !!fullscreenHost);
      button.classList.remove('oc-fs-reveal');
      if (fullscreenHost) {
        try {
          fullscreenHost.appendChild(host);
          fullscreenHost.addEventListener('pointermove', onFsPointerMove as EventListener);
        } catch {
          /* Unsupported fullscreen host. */
        }
      } else if (host.parentNode !== document.body) document.body.appendChild(host);
      applyDock();
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    releaseLauncherChrome = () => {
      clearTimeout(clickReset);
      if (revealTimer) clearTimeout(revealTimer);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerup', up, true);
      window.removeEventListener('pointercancel', cancel, true);
      window.removeEventListener('blur', blur);
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
      fullscreenHost?.removeEventListener('pointermove', onFsPointerMove as EventListener);
      edgeObserver?.disconnect();
    };
  }

  // Hand renderForm a submit handler wrapper so we can intercept success → show success state.
  // This is set up inside resetToForm via the handlers object. Override onSubmit so the UI
  // layer controls the "render success" transition itself.
  const originalOnSubmit = handlers.onSubmit;
  handlers.onSubmit = async (form) => {
    const result = await originalOnSubmit(form);
    if (result.ok && result.referenceCode) {
      // #215 — clear active pin so a follow-up submission doesn't carry
      // the stale pin data. Capture lifecycle: pin → submit → cleared.
      activePin = null;
      // #256: when the server's reconcile loop dropped any attachments, hand
      // showSuccess a partial-info struct so it renders the partial notice
      // line. Total of 0 (no attachments submitted) is treated as a clean
      // success — there's nothing to be partial about.
      const failed = result.attachmentsFailed ?? 0;
      const total = result.attachmentsTotal ?? 0;
      showSuccess(result.referenceCode, failed > 0 && total > 0 ? { failed, total } : undefined);
    }
    return result;
  };

  function open() {
    if (ready && showingSuccess) resetToForm();
    panel.hidden = false;
    restorePanelPos();
    handlers.onOpen();
    // #598 — custom-launcher / programmatic open goes through the same
    // first-open config resolution as the built-in launcher.
    void ensureReady();
  }
  let challengeController: AbortController | null = null;
  function close() {
    assistant?.cancel();
    challengeController?.abort();
    dictationForms.get(panel)?.cancel();
    // #416 — programmatic close() mirrors the bug-button toggle close:
    // hide the panel, fire onClose for capture-flow abort, but DO NOT
    // resetToForm — same-page reopen should preserve text + pin +
    // attachments. The post-submit fresh-form path is handled by the
    // success branch (resetToForm called from showSuccess via
    // handlers.onSubmit), not by close().
    panel.hidden = true;
    handlers.onClose();
  }
  function destroy() {
    assistant?.destroy();
    challengeController?.abort();
    dictationForms.get(panel)?.dispose();
    releaseLauncherChrome();
    host.remove();
  }

  async function showChallenge(input: ChallengeInput): Promise<string> {
    if (challengeController) throw new Error('challenge_already_running');
    const controller = new AbortController();
    challengeController = controller;
    dictationForms.get(panel)?.cancel();
    // Hold the outer geometry while swapping a tall form for a shorter challenge.
    // Existing max-height still bounds this during viewport changes.
    const previousHeight = panel.style.height;
    const measuredHeight = panel.getBoundingClientRect().height;
    const heldHeight = measuredHeight > 0 ? `${measuredHeight}px` : null;
    if (heldHeight) panel.style.height = heldHeight;
    // Keep the same form nodes, values, listeners and attachment state in memory.
    const original = document.createDocumentFragment();
    original.append(...Array.from(panel.childNodes));
    const onPanelClose = () => controller.abort();
    shadow.addEventListener(PANEL_CLOSE_EVENT, onPanelClose);
    let challengeHost: HTMLDivElement | null = null;
    let failed = false;
    try {
      // Temporarily show the challenge UI. Lazy-load the challenge
      // module so this ~3 KB code path isn't in the main bundle. #482: routed
      // through setTrustedHtml so it doesn't throw under host Trusted Types.
      setTrustedHtml(
        panel,
        `
      <div class="panel-header">
        <div class="panel-title" id="oncatch-panel-title">Verifying</div>
      </div>
      <div class="panel-body">
        <div class="challenge-copy">
          <div class="challenge-heading">Quick check — please confirm you&#39;re human.</div>
          <div class="challenge-sub">Protected by Cloudflare. No tracking.</div>
        </div>
        <div class="challenge-host" role="region" aria-live="polite" aria-label="Verification challenge"></div>
        <div class="error" role="alert" hidden></div>
      </div>
      <div class="footer">Powered by OnCatch</div>
    `,
      );
      challengeHost = panel.querySelector<HTMLDivElement>('.challenge-host');
      if (!challengeHost) throw new Error('challenge_host_missing');
      const cancel = document.createElement('button');
      cancel.type = 'button';
      cancel.className = 'btn-secondary';
      cancel.textContent = 'Cancel verification';
      cancel.addEventListener('click', () => controller.abort(), { once: true });
      challengeHost.after(cancel);
      panel.hidden = false;
      const mod = await import('./challenge/turnstile.ts');
      const result = await mod.runChallenge({
        container: challengeHost,
        siteKey: input.siteKey,
        action: input.action,
        cdata: input.cdata,
        // #56: pin Turnstile's UI language to the widget's resolved locale.
        // Without this Cloudflare's bot-check would auto-detect from
        // navigator.language and could disagree with the rest of the widget
        // (e.g., Customer set data-locale="es" but reporter's browser is
        // "en"; we want es throughout).
        language: handlers.locale,
        signal: controller.signal,
      });
      if (controller.signal.aborted) throw new Error('challenge_cancelled');
      return result.token;
    } catch (err) {
      failed = true;
      throw err;
    } finally {
      shadow.removeEventListener(PANEL_CLOSE_EVENT, onPanelClose);
      if (challengeController === controller) {
        challengeController = null;
        if (heldHeight && panel.style.height === heldHeight) panel.style.height = previousHeight;
        if (panel.isConnected && (!challengeHost || panel.contains(challengeHost))) {
          panel.replaceChildren(original);
          if (failed && !controller.signal.aborted)
            showError(panel, "We couldn't load the verification check. Please try again.");
        }
      }
    }
  }

  return { open, close, root: shadow, destroy, showChallenge, applyConfig };
}

/**
 * Exported only for #55 accessibility testing — the audit test mounts the
 * form HTML into a test container and runs axe-core against it without
 * needing access to the closed shadow root used by `mountUi` in production.
 * Not for use outside the package.
 */
export function renderForm(
  panel: HTMLElement,
  handlers: UiHandlers,
  opts: { onAfterRender?: () => void } = {},
) {
  const showAttachments = Boolean(handlers.attachmentManager);
  // #136: when storage is full, render the section but with capture buttons
  // suppressed and the "Attachments unavailable" notice shown instead. Text
  // submission stays enabled — the widget never breaks the customer's flow.
  const storageFull = handlers.storageState === 'full';
  dictationForms.get(panel)?.dispose();
  const m = handlers.messages;

  // #212 PR-B — identification UI block. Active when the workspace toggle
  // is on AND the host app called OnCatch.identify() with a valid payload.
  // Sensitive-data-mode short-circuits to false (server also strips).
  const identificationActive = isIdentificationActive();
  const identity = identificationActive ? getCurrentIdentity() : null;

  // "Save your name and email" opt-in. Suppressed under sensitive-data-mode:
  // that workspace has opted out of reporter data being retained, and keeping
  // a browser-side copy would work around the same decision it enforces
  // server-side. Also needs a workspace id to key storage against — legacy
  // embeds that don't thread one through simply don't get the checkbox.
  const contactPrefillWorkspaceId = handlers.workspaceId ?? '';
  const showContactPrefill = Boolean(contactPrefillWorkspaceId) && !handlers.sensitiveDataMode;
  const reportingAsLabel = identity
    ? identity.name && identity.email
      ? `${identity.name} (${identity.email})`
      : identity.email
        ? identity.email
        : identity.userId
    : '';

  function renderIdentificationBlock(): string {
    if (!identity) return '';
    return `<div class="identified-reporter-row" data-identified="true">
      <span class="identified-reporter-label">Reporting as <strong class="identified-reporter-name"></strong></span>
      <button type="button" class="identified-reporter-toggle" data-not-me>Not me?</button>
    </div>`;
  }
  function reporterFieldHiddenAttr(): string {
    return identity ? 'hidden' : '';
  }
  // #55 a11y: labels wrap their inputs (no for=/id= sprinkling) so SC 1.3.1
  // Info-and-Relationships passes without per-field IDs. The required-marker
  // asterisk is decorative — `required` attribute on the input is what
  // announces required-state to screen readers; aria-hidden on the asterisk
  // span avoids double-announcement ("required required") on some readers.
  // #482: TT-safe assignment. Markup is developer-authored; every interpolated
  // value is escaped via escapeHtml/escapeAttr at its site below.
  setTrustedHtml(
    panel,
    `
    <div class="panel-header">
      <h2 class="panel-title" id="oncatch-panel-title">${escapeHtml(m.panelTitle)}</h2>
      <button type="button" class="panel-close" aria-label="${escapeAttr(m.panelClose)}">×</button>
    </div>
    <div class="panel-body">
      <form novalidate>
        <label class="field">
          <span class="field-label-text">${escapeHtml(m.fieldTitleLabel)} <span class="field-required" aria-hidden="true">*</span></span>
          <input name="title" type="text" maxlength="200" required placeholder="${escapeAttr(m.fieldTitlePlaceholder)}" />
        </label>
        <label class="field">
          <span class="field-label-text">${escapeHtml(m.fieldSummaryLabel)}</span>
          <textarea name="summary" maxlength="5000" placeholder="${escapeAttr(m.fieldSummaryPlaceholder)}"></textarea>
        </label>
        ${showAttachments ? renderAttachmentsSection(storageFull, m, Boolean(handlers.sensitiveDataMode), handlers.attachmentManager?.acceptsSourceTextUploads?.() === true) : ''}
        <!-- #212 PR-B — when identification is active, hide email/name behind
             a "Reporting as X" line + "Not me?" toggle. The label DIVs always
             render; the manual inputs are hidden initially when identified. -->
        ${renderIdentificationBlock()}
        <label class="field reporter-name-field" ${reporterFieldHiddenAttr()}>
          <span class="field-label-text">${escapeHtml(m.fieldNameLabel)}</span>
          <input name="reporterName" type="text" maxlength="120" autocomplete="name" />
        </label>
        <label class="field reporter-email-field" ${reporterFieldHiddenAttr()}>
          <span class="field-label-text">${escapeHtml(m.fieldEmailLabel)}</span>
          <input name="reporterEmail" type="email" maxlength="200" autocomplete="email" />
        </label>
        ${
          handlers.fixNotificationsEnabled
            ? `<label class="field-checkbox notify-fix-field" hidden>
          <input name="notifyOnFix" type="checkbox" />
          <span class="field-checkbox-text">${escapeHtml(m.fieldNotifyOnFixLabel)}</span>
        </label>`
            : ''
        }
        <div class="reporter-preferences">
        ${
          showContactPrefill
            ? `<label class="field-checkbox reporter-save-contact-field" ${reporterFieldHiddenAttr()}>
          <input name="saveContact" type="checkbox" />
          <span class="field-checkbox-text">${escapeHtml(m.fieldSaveContactLabel)}</span>
        </label>
        <div class="field-checkbox-notice" data-save-contact-unavailable role="status" hidden>${escapeHtml(m.fieldSaveContactUnavailable)}</div>`
            : ''
        }
        </div>
        <button type="submit" class="submit">${escapeHtml(m.buttonSend)}</button>
        <div class="error" role="alert" aria-live="assertive" hidden></div>
      </form>
    </div>
    <div class="footer">${escapeHtml(m.poweredBy)}</div>
  `,
  );

  const closeBtn = panel.querySelector<HTMLButtonElement>('.panel-close');
  closeBtn?.addEventListener('click', () => {
    panel.hidden = true;
    // Keep aria-expanded in sync so the bug button's state reflects panel state
    // regardless of which close affordance the user hit. The bug button (`.btn`)
    // is mounted in mountUi() at the shadow-root level, so walk up via
    // getRootNode() (panel lives inside the same shadow).
    const root = panel.getRootNode();
    if (root instanceof ShadowRoot) {
      root.querySelector<HTMLButtonElement>('.btn')?.setAttribute('aria-expanded', 'false');
      // #210 — notify annotation flow (or any other subscriber) that the
      // panel closed mid-flow so it can settle with the right outcome.
      root.dispatchEvent(new CustomEvent(PANEL_CLOSE_EVENT));
    }
    handlers.onClose();
  });

  if (showAttachments && handlers.attachmentManager && !storageFull) {
    // #136: only wire interactive controls when capture is actually available.
    // The "full" branch renders a static notice (no buttons to wire) and
    // text-only submission still works through the unchanged form code below.
    wireAttachmentsSection(
      panel,
      handlers.attachmentManager,
      { workspaceName: handlers.workspaceName, retentionDays: handlers.attachmentRetentionDays },
      {
        widgetBaseUrl: handlers.widgetBaseUrl,
        apiBaseUrl: handlers.apiBaseUrl,
        getToken: handlers.getHostedRecorderToken,
        workspaceId: handlers.workspaceId,
      },
      handlers.onWidgetEvent,
      handlers.privacyConfig,
      m,
      handlers.locale,
      Boolean(handlers.sensitiveDataMode),
    );
  }

  const form = panel.querySelector<HTMLFormElement>('form');
  if (!form) return;

  // #212 PR-B — wire identification UI: render the "Reporting as" name via
  // textContent (XSS-safe — host app's identify() data is attacker-
  // controllable from the customer's app perspective and must not be
  // injected as HTML) + the "Not me?" toggle that unhides the manual
  // email/name fields.
  if (identity) {
    const nameSlot = form.querySelector<HTMLElement>('.identified-reporter-name');
    if (nameSlot) nameSlot.textContent = reportingAsLabel;
    const notMeBtn = form.querySelector<HTMLButtonElement>('button[data-not-me]');
    const nameField = form.querySelector<HTMLElement>('.reporter-name-field');
    const emailField = form.querySelector<HTMLElement>('.reporter-email-field');
    notMeBtn?.addEventListener('click', () => {
      nameField?.removeAttribute('hidden');
      emailField?.removeAttribute('hidden');
      notMeBtn?.setAttribute('hidden', '');
    });
  }

  // Visual validation: when a required field is empty on submit, add a
  // .field-invalid class that paints a red border. Cleared the moment the
  // user starts typing in that field. The bottom-of-form text message stays
  // (it's the screen-reader signal); this is the visual layer for sighted users.
  const titleInput = form.querySelector<HTMLInputElement>('input[name="title"]');
  const summaryInput = form.querySelector<HTMLTextAreaElement>('textarea[name="summary"]');
  titleInput?.addEventListener('input', () => titleInput.classList.remove('field-invalid'));
  if (handlers.dictationEnabled && !handlers.sensitiveDataMode && handlers.transcribeDraft) {
    dictationForms.set(panel, installDictation(panel, handlers.locale, handlers.transcribeDraft));
  } else dictationForms.delete(panel);
  summaryInput?.addEventListener('input', () => summaryInput.classList.remove('field-invalid'));

  // Tight email regex — RFC 5322 is overkill for a reporter contact field.
  // We accept anything that has a non-empty local part, an @, a non-empty
  // domain with at least one dot, and a 2+ letter TLD. Server-side Zod
  // (`z.string().email()`) is the strict authority; this is a friendly
  // pre-flight to keep obviously-broken input from triggering a server
  // round trip whose error currently maps to a generic "something went
  // wrong" toast.
  const REPORTER_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;
  const emailInput = form.querySelector<HTMLInputElement>('input[name="reporterEmail"]');
  emailInput?.addEventListener('input', () => emailInput.classList.remove('field-invalid'));

  // #648 (OC-5) — "Notify me when this is fixed" is only meaningful with an
  // email, so the checkbox tracks the email field's non-emptiness. Computed
  // from the CURRENT value (not just input events) because contact prefill
  // sets emailInput.value programmatically — no input event fires — and the
  // returning reporters it prefils are exactly the population most likely to
  // opt in. Called: now (initial render), after the prefill block below, and
  // on every email keystroke.
  const notifyFixField = form.querySelector<HTMLElement>('.notify-fix-field');
  const notifyFixInput = form.querySelector<HTMLInputElement>('input[name="notifyOnFix"]');
  const syncNotifyFixVisibility = () => {
    if (!notifyFixField) return;
    if (emailInput && emailInput.value.trim().length > 0) {
      notifyFixField.removeAttribute('hidden');
    } else {
      notifyFixField.setAttribute('hidden', '');
    }
  };
  emailInput?.addEventListener('input', syncNotifyFixVisibility);
  syncNotifyFixVisibility();

  // Opt-in contact prefill. On open, a previously-saved name/email repopulates
  // both fields and re-ticks the box. Unticking wipes the stored copy right
  // away rather than at next submit, so "I cleared it" and "it is gone" are
  // the same moment — a reporter who unticks and closes the panel has still
  // had their data removed.
  const nameInput = form.querySelector<HTMLInputElement>('input[name="reporterName"]');
  const saveContactInput = form.querySelector<HTMLInputElement>('input[name="saveContact"]');
  if (showContactPrefill && saveContactInput) {
    const saved = readStoredContact(contactPrefillWorkspaceId);
    if (saved) {
      if (nameInput && !nameInput.value) nameInput.value = saved.name;
      if (emailInput && !emailInput.value) emailInput.value = saved.email;
      saveContactInput.checked = true;
      // #648 — programmatic value assignment fires no input event; re-sync so
      // prefilled reporters see the notify checkbox without touching the field.
      syncNotifyFixVisibility();
    }
    const unavailableNotice = form.querySelector<HTMLElement>('[data-save-contact-unavailable]');
    saveContactInput.addEventListener('change', () => {
      if (!saveContactInput.checked) {
        clearStoredContact(contactPrefillWorkspaceId);
        if (unavailableNotice) unavailableNotice.hidden = true;
        return;
      }
      // Probe at TICK time, not at save time. Storage that throws (Safari
      // private mode) or silently drops writes would otherwise leave the box
      // looking accepted while nothing was ever stored — the reporter would
      // only discover it on a later visit that failed to prefill. Untick it
      // so the control never claims a guarantee it cannot keep.
      if (!isContactStorageAvailable()) {
        saveContactInput.checked = false;
        if (unavailableNotice) unavailableNotice.hidden = false;
      }
    });
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (dictationForms.get(panel)?.busy()) return;
    mark(WidgetMark.submitStart);

    const submitBtn = form.querySelector<HTMLButtonElement>('.submit');
    const mgr = handlers.attachmentManager;

    // #533 (F3) — read + validate the CURRENT field values. Called TWICE: once
    // for fast pre-gate feedback at click time, and again immediately before
    // onSubmit. The second read is not redundant: the fields stay editable
    // during the settle wait below (that is deliberate — the reporter should be
    // able to keep typing while their screenshot uploads), so submitting the
    // click-time snapshot would silently discard everything they typed while
    // waiting. Re-validating on the re-read also means a field emptied during
    // the wait blocks with the same inline error instead of submitting stale
    // text.
    const readAndValidate = (): {
      title: string;
      summary: string;
      reporterName: string;
      reporterEmail: string;
      notifyOnFix: boolean;
    } | null => {
      const data = new FormData(form);
      const title = (data.get('title') as string | null)?.trim() ?? '';
      const summary = (data.get('summary') as string | null)?.trim() ?? '';
      const reporterEmail = ((data.get('reporterEmail') as string | null) ?? '').trim();

      // Details (summary) is optional — the title is the only field that can
      // block a submission. An empty summary submits as an empty string rather
      // than null, which keeps every downstream consumer (webhook v1's
      // `summary: z.string()`, the search tsv, the dedup fingerprint) working
      // on a plain string with no nullability to handle.
      if (!title) {
        if (titleInput) {
          titleInput.classList.add('field-invalid');
          titleInput.focus();
        }
        showError(panel, m.errorTitleRequired);
        return null;
      }

      // Email is optional. If supplied, it must be well-formed — surface a
      // specific inline error instead of the server's generic "something went
      // wrong" mapping (which previously rendered for any Zod schema failure
      // including reporter-email). Only fires on non-empty input.
      if (reporterEmail.length > 0 && !REPORTER_EMAIL_PATTERN.test(reporterEmail)) {
        if (emailInput) {
          emailInput.classList.add('field-invalid');
          emailInput.focus();
        }
        showError(panel, "The email doesn't look right, please fix or leave blank.");
        return null;
      }

      return {
        title,
        summary,
        reporterName: ((data.get('reporterName') as string | null) ?? '').trim(),
        reporterEmail,
        // #648 — double-guarded: checkbox state AND a non-empty email (a
        // checked-then-hidden checkbox after the email was cleared must not
        // submit an opt-in the server would discard anyway).
        notifyOnFix: Boolean(notifyFixInput?.checked) && reporterEmail.length > 0,
      };
    };

    // 1. Fast pre-gate validation on the click-time values. Nothing is disabled
    //    yet, so a validation failure leaves the form fully interactive.
    if (!readAndValidate()) return;

    // 2. #161: if any attachment slot is in failed state, surface the blocker
    //    modal BEFORE the network submit so the user can decide whether to
    //    retry the upload or proceed without it. This is the explicit
    //    submit-blocker required by AC and design A8.
    if (mgr?.hasFailedSlots() && !mgr.usesBackgroundProcessing()) {
      showAttachmentBlockerModal(panel, mgr, () => {
        // After modal resolves (retry or remove-and-proceed), the user clicks
        // Submit again. This avoids auto-submit-after-modal which can fire
        // before the user has a chance to verify the slot state.
      });
      return;
    }

    // 3. #533 — the settle gate. An attachment still compressing / reserving /
    //    uploading used to be dropped from the submission silently: the
    //    reporter got a green success state with their screenshot missing, and
    //    admin got no signal at all (the #256 partial-failure notice only
    //    reports reservations that were actually sent).
    //
    //    Only the SUBMIT BUTTON is disabled during the wait. Fields stay
    //    editable by design, and slot Remove buttons stay live because Remove
    //    is the reporter's escape hatch out of a stuck upload. The wait always
    //    terminates: every upload settles in bounded time now that the proxy
    //    PUT carries an abort ceiling (api.ts), and Remove settles a slot
    //    instantly.
    if (mgr?.hasPendingUploads() && !mgr.usesBackgroundProcessing()) {
      if (submitBtn) setSubmitBusy(submitBtn, m.buttonWaitingForUploads);
      mark(WidgetMark.uploadsSettleStart);
      await mgr.waitForUploadsToSettle();
      mark(WidgetMark.uploadsSettleEnd);
      measure('uploads-settle', WidgetMark.uploadsSettleStart, WidgetMark.uploadsSettleEnd);

      // Settled-failed → the existing blocker-modal path. Settled-uploaded →
      // fall through and the reservation rides along in the submission.
      if (mgr.hasFailedSlots()) {
        if (submitBtn) restoreSubmitButton(submitBtn, m.buttonSend);
        showAttachmentBlockerModal(panel, mgr, () => {
          // Same contract as the pre-gate modal above.
        });
        return;
      }
    }

    // 4. #533 (F3) — re-read the values AFTER the gate, immediately before
    //    onSubmit, so edits made during the wait are honored.
    const values = readAndValidate();
    if (!values) {
      if (submitBtn) restoreSubmitButton(submitBtn, m.buttonSend);
      return;
    }

    // 5. Clear to send. The WHOLE form disables, not just the button: attachment
    //    capture/remove controls stayed live before, which both looked broken
    //    and let a slot mutate between prepareForSubmit and the POST.
    setFormBusy(form, true);
    if (submitBtn) {
      // #55 a11y: aria-busy lets screen readers announce the in-flight state.
      // Without it the textContent flip ("Send report" → "Sending…") is a
      // silent change because the button still has focus and just-text changes
      // don't always re-announce on every reader.
      setSubmitBusy(submitBtn, m.buttonSending);
    }

    const result = await handlers.onSubmit({
      title: values.title,
      summary: values.summary,
      reporterName: values.reporterName,
      reporterEmail: values.reporterEmail,
      notifyOnFix: values.notifyOnFix,
      // #215 — pin capture data when the user submitted via pin mode.
      // undefined when no pin was captured this session.
      ...(activePin ? { pinned: activePin } : {}),
    });
    mark(WidgetMark.submitEnd);
    measure('submit', WidgetMark.submitStart, WidgetMark.submitEnd);

    if (!result.ok || !result.referenceCode) {
      setFormBusy(form, false);
      if (submitBtn) restoreSubmitButton(submitBtn, m.buttonSend);
      showError(panel, friendlyError(result.error));
    } else if (showContactPrefill && saveContactInput?.checked) {
      // Persist only on a submission that actually landed, and only from the
      // values that were sent — so what gets prefilled next time is exactly
      // what the workspace received, not a half-typed intermediate state.
      writeStoredContact(contactPrefillWorkspaceId, {
        name: values.reporterName,
        email: values.reporterEmail,
      });
    }
    // Success branch is handled in mountUi's wrapped onSubmit, which calls showSuccess().
    // Don't render success from here — that would double-wrap.
  });

  opts.onAfterRender?.();
}

// ============================================================================
// #161: ATTACHMENTS UI
// ============================================================================

function renderAttachmentsSection(
  storageFull: boolean,
  m: WidgetMessages,
  sensitiveDataMode: boolean,
  sourceTextEnabled = false,
): string {
  if (storageFull) {
    // #136: cap-full degradation. Same visual slot — header + a clearly-styled
    // notice instead of buttons — so users on workspaces that just hit the cap
    // see WHY screenshots aren't there, not a mysteriously-vanished section.
    // Text submission below stays enabled so the customer's product never
    // breaks: every reporter can still file feedback.
    return `
      <div class="attachments" data-att-storage-full>
        <div class="attachments-header">
          <span class="attachments-label">${escapeHtml(m.attachmentsLabel)}</span>
        </div>
        <div class="att-storage-full" role="status">
          ${ATT_STORAGE_FULL_ICON}<span>${escapeHtml(m.storageFullDetailedNotice)}</span>
        </div>
      </div>
    `;
  }
  return `
    <div class="attachments">
      <div class="attachments-header">
        <span class="attachments-label">${escapeHtml(m.attachmentsLabel)}</span>
        <span class="attachments-count" data-att-count></span>
      </div>
      <div class="attachments-controls">
        <button type="button" class="attach-btn" data-att-action="capture-page">
          ${ATT_SCREENSHOT_ICON}<span>${escapeHtml(m.captureWholePageBtn)}</span>
        </button>
        <button type="button" class="attach-btn" data-att-action="capture-region">
          ${ATT_REGION_ICON}<span>${escapeHtml(m.captureSelectRegionBtn)}</span>
        </button>
        <button type="button" class="attach-btn" data-att-action="pin-element" data-pin-hidden-by-default hidden>
          ${ATT_PIN_ICON}<span>${escapeHtml(m.captureSelectElementBtn)}</span>
        </button>
        <button type="button" class="attach-btn" data-att-action="record-screen" data-hidden-by-default hidden>
          ${ATT_RECORD_ICON}<span>${escapeHtml(m.captureRecordBtn)}</span>
        </button>
      </div>
      <div class="att-record-unsupported" data-att-record-unsupported role="status" hidden>
        ${escapeHtml(m.recordingUnsupportedInline)}
      </div>
      <div class="att-camera-denied" data-att-camera-denied role="status" hidden>
        ${escapeHtml(m.cameraDeniedFallback)}
      </div>
      ${
        // #605 — upload dropzone. Not emitted under sensitiveDataMode (no
        // banner either: the reason is workspace policy, not browser support
        // — same reasoning as the Record button's suppression). Uploads
        // bypass privacy-mask.ts, so a workspace that opted into masking
        // must not get an unmasked side door.
        sensitiveDataMode
          ? ''
          : `
      <div class="att-dropzone" data-att-dropzone>
        <input type="file" class="att-dropzone-input" data-att-file-input
               accept="image/png,image/jpeg,image/webp,.pdf,.docx${sourceTextEnabled ? SOURCE_TEXT_EXTENSIONS.map((ext) => `,.${ext}`).join('') : ''}" multiple hidden />
        ${ATT_UPLOAD_ICON}
        <button type="button" class="att-dropzone-btn" data-att-action="upload-file"
                aria-describedby="oncatch-att-dropzone-hint">
          ${escapeHtml(m.uploadDropzoneBtn)}
        </button>
        <span class="att-dropzone-hint" id="oncatch-att-dropzone-hint" data-att-dropzone-hint></span>
        <div class="att-dropzone-error" data-att-dropzone-error role="status" aria-live="polite" hidden></div>
      </div>`
      }
      <div class="attachments-slots" data-att-slots></div>
    </div>
  `;
}

const ATT_SCREENSHOT_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/><circle cx="12" cy="12" r="3"/></svg>`;
const ATT_REGION_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 3a2 2 0 0 0-2 2"/><path d="M19 3a2 2 0 0 1 2 2"/><path d="M21 19a2 2 0 0 1-2 2"/><path d="M5 21a2 2 0 0 1-2-2"/><path d="M9 3h1"/><path d="M9 21h1"/><path d="M14 3h1"/><path d="M14 21h1"/><path d="M3 9v1"/><path d="M21 9v1"/><path d="M3 14v1"/><path d="M21 14v1"/></svg>`;
const ATT_RECORD_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m22 8-6 4 6 4V8Z"/><rect width="14" height="12" x="2" y="6" rx="2" ry="2"/></svg>`;
// #215 — Pin icon (crosshair) for the Pin button.
const ATT_PIN_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><line x1="12" y1="3" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="21"/><line x1="3" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="21" y2="12"/></svg>`;
const ATT_STORAGE_FULL_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>`;
// #605 — upload glyph for the dropzone. The storage-full icon is already a
// tray-with-up-arrow with aria-hidden set; alias it rather than shipping a
// duplicate SVG path (zero bundle bytes). Declared after it (module-level
// const TDZ).
const ATT_UPLOAD_ICON = ATT_STORAGE_FULL_ICON;

function wireAttachmentsSection(
  panel: HTMLElement,
  mgr: AttachmentManager,
  recordingContext?: { workspaceName?: string; retentionDays?: number },
  hosted?: {
    widgetBaseUrl?: string;
    apiBaseUrl?: string;
    getToken?: () => Promise<string | null>;
    workspaceId?: string;
  },
  onWidgetEvent?: UiHandlers['onWidgetEvent'],
  privacyConfig?: { maskSelectors: string[]; redactPatterns: string[] },
  messages?: WidgetMessages,
  locale?: SupportedLocale,
  sensitiveDataMode = false,
): void {
  const slotsEl = panel.querySelector<HTMLDivElement>('[data-att-slots]');
  const countEl = panel.querySelector<HTMLSpanElement>('[data-att-count]');
  const captureBtn = panel.querySelector<HTMLButtonElement>('[data-att-action="capture-page"]');
  const regionBtn = panel.querySelector<HTMLButtonElement>('[data-att-action="capture-region"]');
  const recordBtn = panel.querySelector<HTMLButtonElement>('[data-att-action="record-screen"]');
  // #215 — Pin button. Hidden on touch-only devices since pin mode requires
  // a fine-pointer cursor. Detection runs once at mount; if pointer-fine is
  // present we reveal the button. matchMedia listener handles laptops that
  // plug in a mouse later (revealed; never re-hidden mid-session).
  const pinBtn = panel.querySelector<HTMLButtonElement>('[data-att-action="pin-element"]');
  // #605 — upload dropzone elements. All null under sensitiveDataMode (the
  // markup is not emitted) and in the storage-full branch; every use below
  // is null-guarded because render() runs in both of those states too.
  const dropzone = panel.querySelector<HTMLDivElement>('[data-att-dropzone]');
  const fileInput = panel.querySelector<HTMLInputElement>('[data-att-file-input]');
  const uploadBtn = panel.querySelector<HTMLButtonElement>('[data-att-action="upload-file"]');
  const dropzoneHint = panel.querySelector<HTMLSpanElement>('[data-att-dropzone-hint]');
  const dropzoneError = panel.querySelector<HTMLDivElement>('[data-att-dropzone-error]');
  if (pinBtn) {
    try {
      const fineMq = window.matchMedia?.('(pointer: fine)');
      if (fineMq?.matches) pinBtn.hidden = false;
      fineMq?.addEventListener?.('change', (e: MediaQueryListEvent) => {
        if (e.matches) pinBtn.hidden = false;
      });
    } catch {
      // matchMedia not available — leave hidden (older browsers / iframes
      // with restricted APIs). Pin mode requires the API anyway.
    }
  }
  // #297 — camera capture is now an opt-in INSIDE the recording consent modal
  // rather than a separate capture button. This flag records whether the
  // browser can actually reach a camera, and gates whether the modal offers
  // the "Include camera" checkbox at all. Resolved asynchronously with the
  // screen-support check below; stays false when that check never lands, so
  // the modal degrades to screen-only rather than offering a control that
  // would fail on click.
  let captureAvailability: CaptureChoiceAvailability = {
    screen: {
      kind: 'unavailable',
      explanation: 'Screen recording is not available in this browser.',
    },
    camera: {
      kind: 'unavailable',
      explanation: 'Camera recording is not available in this browser.',
    },
    audio: {
      kind: 'unavailable',
      explanation: 'Audio recording is not available in this browser.',
    },
  };
  if (!slotsEl || !countEl || !captureBtn || !regionBtn) return;

  // #163 PR B — show the Record button only when the browser supports
  // MediaRecorder + getDisplayMedia + a usable codec. Safari iOS 17.0-17.3,
  // older browsers, and desktop Safari without screen-capture entitlement
  // all return supported=false here.
  // #163 PR C — when unsupported, surface an inline banner below the
  // controls so users understand why the Record button is missing
  // (Compliance + a11y discoverability — silent omission breaks the
  // "what can I attach?" mental model).
  const unsupportedBanner = panel.querySelector<HTMLDivElement>('[data-att-record-unsupported]');
  // Sensitive-data-mode short-circuits the entire recording check. The
  // workspace has opted out of screen recording wholesale; we don't even
  // load the screen-recording-client chunk (saves bandwidth on every
  // sensitive-mode page), don't show the Record button, and don't show
  // the "unsupported" banner (the reason isn't browser support — it's
  // workspace policy — so the banner copy would be wrong here).
  if (recordBtn && !sensitiveDataMode) {
    try {
      void Promise.all([
        import('./screen-recording-client.ts'),
        import('./audio-recording-client.ts'),
      ]).then(([screenMod, audioMod]) => {
        const support = screenMod.checkRecordingSupport();
        const audioSupport = audioMod.checkAudioRecordingSupport();
        const cameraSupport = screenMod.checkCameraRecordingSupport();
        const intrinsicCamera = screenMod.checkCameraRecordingSupport({
          ignorePermissionsPolicy: true,
        });
        const intrinsicAudio = audioMod.checkAudioRecordingSupport({
          ignorePermissionsPolicy: true,
        });
        const unavailable = (label: string): CaptureAvailability => ({
          kind: 'unavailable',
          explanation: `${label} is not available in this browser.`,
        });
        captureAvailability = {
          screen: support.supported ? { kind: 'inline' } : unavailable('Screen recording'),
          camera: cameraSupport.supported
            ? { kind: 'inline' }
            : (cameraSupport.reason === 'camera_blocked' ||
                  cameraSupport.reason === 'display_capture_blocked') &&
                intrinsicCamera.supported
              ? {
                  kind: 'hosted',
                  explanation:
                    'This page blocks inline recording. OnCatch will open a secure recorder.',
                }
              : unavailable('Screen recording with camera'),
          audio: audioSupport.supported
            ? { kind: 'inline' }
            : audioSupport.reason === 'microphone_blocked' && intrinsicAudio.supported
              ? {
                  kind: 'hosted',
                  explanation:
                    'This page blocks inline recording. OnCatch will open a secure recorder.',
                }
              : unavailable('Audio recording'),
        };
        if (
          Object.values(captureAvailability).some(
            (availability) =>
              typeof availability !== 'boolean' && availability.kind !== 'unavailable',
          )
        ) {
          recordBtn.hidden = false;
        } else if (
          unsupportedBanner &&
          (support.reason === 'no_get_display_media' ||
            support.reason === 'no_supported_codec' ||
            support.reason === 'no_media_recorder' ||
            // #485: a restrictive host Permissions-Policy blocked
            // display-capture. Same inline "recording unavailable" notice so
            // the reporter learns why the Record button is missing instead of
            // it silently vanishing (a11y discoverability + the existing
            // "what can I attach?" mental model).
            support.reason === 'display_capture_blocked')
        ) {
          unsupportedBanner.hidden = false;
        }
        // #297 — camera-support gate, same module load (no extra round trip).
        // Only meaningful when screen recording itself is available, since
        // camera recording composites onto the screen capture. When the
        // camera is unavailable (e.g. an iframe without allow="camera"), the
        // Record button still works and the consent modal simply omits the
        // "Include camera" option.
      });
    } catch {
      // If the chunk fails to load, leave both states hidden — the failure
      // mode reads as "no recording features at all," which is correct.
    }
  }

  const render = () => {
    countEl.textContent = `${mgr.count()}/5`;
    // #482: slot markup is developer-authored (filenames are escaped in
    // slotMarkup); route through setTrustedHtml so re-renders don't throw
    // under host Trusted Types.
    setTrustedHtml(
      slotsEl,
      mgr
        .getSlots()
        .map((s) => slotMarkup(s))
        .join(''),
    );

    // Each button is gated only on its OWN busy flag + slot capacity.
    // Cross-gating ("if either is busy, disable both") was broken because the
    // last render fired during the in-flight cycle stuck the OTHER button
    // disabled — and no render fires after the finally cleared the busy flag.
    // Concurrent clicks aren't a real concern in practice: all three capture
    // paths hide the widget host while running, so the user can't reach the
    // other button mid-capture. addFile()/addRecordingResult() are also
    // synchronous on their slot-cap check, so two racing adds can't both
    // push past 5 slots.
    const remaining = mgr.remaining();
    captureBtn.disabled = remaining === 0 || captureBtn.dataset.busy === 'true';
    regionBtn.disabled = remaining === 0 || regionBtn.dataset.busy === 'true';
    // #215 / #398 — Pin button gates on its OWN busy flag + slot capacity.
    // Cross-gating on captureBtn / regionBtn was buggy for the same reason
    // the comment above documents — render fires during the in-flight cycle
    // (when mgr.addFile triggers the subscribe callback), at which point
    // captureBtn.dataset.busy / regionBtn.dataset.busy is still 'true'. The
    // finally block at the end of those capture flows clears the busy flag
    // + directly sets captureBtn.disabled / regionBtn.disabled, but does
    // NOT trigger another render — so pinBtn stayed stuck disabled until
    // the next mgr.notify (e.g. an attachment removal). The fix is the same
    // shape as the de-cross-gating already applied for capture/region: each
    // button gates only on its OWN busy state.
    if (pinBtn) {
      pinBtn.disabled = remaining === 0 || pinBtn.dataset.busy === 'true';
    }
    // One recording per submission, regardless of screen/camera/audio mode.
    const recordingsFull = recordingCount(mgr) >= 1;
    if (recordBtn) {
      recordBtn.disabled = remaining === 0 || recordingsFull || recordBtn.dataset.busy === 'true';
    }
    // #605 — dropzone cap state. Button disables at cap; the hint swaps to
    // the cap-reached string so the state is explained, not mysterious.
    // Null-guarded: the dropzone doesn't exist under sensitiveDataMode.
    if (uploadBtn && dropzoneHint && messages) {
      // The fileInput.disabled term keeps a mid-submit render (e.g. a TTL
      // re-reserve notify inside the setFormBusy window) from re-enabling
      // the button while the form is frozen — the ingest would silently
      // swallow the picked file.
      uploadBtn.disabled = remaining === 0 || fileInput?.disabled === true;
      dropzoneHint.textContent =
        remaining === 0
          ? messages.uploadDropzoneCapReached
          : messages.uploadDropzoneHint(remaining);
      dropzoneHint.hidden = false;
    }

    if (panel.querySelector('form')?.dataset.submitting === 'true') {
      for (const control of panel.querySelectorAll<HTMLInputElement | HTMLButtonElement>(
        'input,button',
      ))
        control.disabled = true;
    }

    slotsEl.querySelectorAll<HTMLButtonElement>('[data-att-slot-action]').forEach((btn) => {
      const slotId = btn.getAttribute('data-att-slot-id') ?? '';
      const action = btn.getAttribute('data-att-slot-action');
      btn.addEventListener('click', () => {
        if (action === 'remove') {
          void mgr.remove(slotId);
        } else if (action === 'retry') {
          void mgr.retry(slotId);
        }
      });
    });
  };

  // ==========================================================================
  // #605 — upload-your-own-screenshot dropzone wiring. Three entry paths
  // (picker / drop / paste) funnel into one ingest function so validation
  // can't drift between them. Everything is attached inside the
  // sensitive-mode guard below — under sensitiveDataMode the markup isn't
  // rendered AND no listener (including panel-level paste) ever exists, so
  // there is no back door around the suppressed UI.
  // ==========================================================================

  const showDropzoneError = (msg: string): void => {
    if (!dropzoneError) return;
    dropzoneError.textContent = msg;
    dropzoneError.hidden = false;
  };
  const hideDropzoneError = (): void => {
    if (dropzoneError) dropzoneError.hidden = true;
  };
  const handleAddFileError = (err: unknown): void => {
    if (!messages) return;
    if (err instanceof AttachmentValidationError) {
      showDropzoneError(
        err.reason === 'source_text_too_large'
          ? 'Text files must be 1 MB or smaller.'
          : err.reason === 'file_too_large'
            ? messages.uploadErrorFileTooLarge
            : err.reason === 'max_slots_reached'
              ? messages.uploadDropzoneCapReached
              : messages.uploadErrorUnsupportedType,
      );
      return;
    }
    // addFile only throws typed validation errors before any network work;
    // post-reserve failures render per-slot (failed tile + Retry), not here.
    // This branch is defensive — a generic string, NOT the type-rejection
    // one, so an unexpected escape never tells the reporter their file type
    // was wrong when it wasn't.
    showDropzoneError(messages.uploadErrorGeneric);
  };

  // Picker, drop and paste share a convenience allowlist. The server is the
  // security boundary: binaries are scanned/rebuilt; source text remains inert. Videos remain
  // recording-only; image/jpg normalizes to image/jpeg.
  const isUploadableFile = (f: File): boolean => {
    if (mgr.acceptsSourceTextUploads?.() && normalizeSourceTextUpload(f.name, f.type)) return true;
    const mime = f.type.toLowerCase();
    if (['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(mime)) return true;
    return (
      (mime === 'application/pdf' && /\.pdf$/i.test(f.name)) ||
      (mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' &&
        /\.docx$/i.test(f.name))
    );
  };

  let ingestBusy = false;
  const ingestUploadFiles = async (files: File[]): Promise<void> => {
    if (!messages || files.length === 0) return;
    // Annotation overlay open — ignore new input until it settles. ingestBusy
    // covers the dropzone's own overlay; the querySelector covers the
    // whole-page / region capture overlays, which don't set that flag. A
    // paste inside an open overlay's text-tool input bubbles to the panel
    // listener, and without this check it would stack a second fullscreen
    // overlay on top of the first.
    if (ingestBusy || panel.querySelector('.annotate-overlay')) return;
    // setFormBusy disables every input during submit so slots can't mutate
    // between prepareForSubmit and the POST (#533's invariant). Drop/paste
    // aren't buttons, so honor the same freeze via the hidden input's
    // disabled state.
    if (fileInput?.disabled) return;
    hideDropzoneError();
    const images = files.filter(isUploadableFile);
    if (images.length === 0) {
      showDropzoneError(messages.uploadErrorUnsupportedType);
      return;
    }
    if (images.length < files.length) {
      showDropzoneError(messages.uploadErrorUnsupportedType);
    }
    const room = mgr.remaining();
    if (room === 0) {
      showDropzoneError(messages.uploadDropzoneCapReached);
      return;
    }
    const toAdd = images.slice(0, room).filter((file) => {
      if (normalizeSourceTextUpload(file.name, file.type) && file.size > SOURCE_TEXT_MAX_BYTES) {
        showDropzoneError('Text files must be 1 MB or smaller.');
        return false;
      }
      if (file.type.startsWith('application/') && file.size > 4 * 1024 * 1024) {
        showDropzoneError('Documents must be 4 MB or smaller.');
        return false;
      }
      return true;
    });
    if (images.length > room) {
      showDropzoneError(messages.uploadErrorTooMany(images.length - room));
    }

    const single = toAdd.length === 1 ? toAdd[0] : undefined;
    if (single?.type.startsWith('image/')) {
      // Pre-check size so the reporter doesn't annotate a file addFile is
      // guaranteed to reject afterwards.
      if (single.size > MAX_PRE_COMPRESSION_BYTES) {
        showDropzoneError(messages.uploadErrorFileTooLarge);
        return;
      }
      // A single upload gets the annotation flow, same as whole-page capture.
      // runAnnotationFlow self-skips on touch-only devices (returns 'skip').
      // Focus-restore target is wherever the reporter actually was (the
      // textarea for a paste, the button for a click) — read via getRootNode
      // because document.activeElement returns the shadow host. Guarded:
      // focus restore is a nicety, and happy-dom's ShadowRoot.activeElement
      // getter throws when the last-focused element was detached (which
      // #598's placeholder→form re-render does on first open); browsers
      // return null there per spec. Fall back to the upload button.
      let active: Element | null = null;
      try {
        const rootNode = panel.getRootNode();
        active = rootNode instanceof ShadowRoot ? rootNode.activeElement : document.activeElement;
      } catch {
        // Leave active null; focus restore targets the upload button.
      }
      ingestBusy = true;
      try {
        const result = await runAnnotationFlow(
          single,
          panel,
          messages,
          makePanelCloseSubscriber(panel),
          active instanceof HTMLElement ? active : (uploadBtn ?? undefined),
        );
        if (
          (result.outcome === 'save' || result.outcome === 'skip') &&
          panel.querySelector('form')?.dataset.submitting !== 'true'
        ) {
          // NO skipImageCompression — user uploads are not widget-generated
          // PNGs, so they get the WebP re-encode (#333's intended split).
          await mgr.addFile(result.blob, single.name).catch(handleAddFileError);
        }
        // 'discard' / 'error' → no slot; the reporter can re-upload.
      } finally {
        ingestBusy = false;
      }
    } else {
      // Multi-file batch: annotation is deliberately skipped — the modal is
      // a fullscreen one-image flow, and chaining N of them serially is
      // hostile (ambiguous Esc, no queue visibility). Upload one at a time
      // to annotate each. addFile's slot-cap check is synchronous, so these
      // fire-and-forget adds can't race past the cap.
      for (const file of toAdd) {
        void mgr.addFile(file, file.name).catch(handleAddFileError);
      }
    }
  };

  // Paste fires on the focused element and bubbles; a panel-level listener
  // catches an OS-screenshot paste no matter which field has focus, while a
  // pure-text paste (clipboardData.files empty) returns before
  // preventDefault and reaches the browser default untouched. The panel
  // element survives form re-renders, so the observer teardown below removes
  // this listener to avoid stacking one copy per re-render.
  const onPaste = (e: ClipboardEvent): void => {
    const files = Array.from(e.clipboardData?.files ?? []);
    if (files.length === 0) return;
    if (!files.some(isUploadableFile)) {
      // e.g. a pasted video file — reject visibly, never queue silently.
      if (messages) showDropzoneError(messages.uploadErrorUnsupportedType);
      return;
    }
    e.preventDefault();
    void ingestUploadFiles(files);
  };

  if (!sensitiveDataMode && dropzone && fileInput && uploadBtn && messages) {
    uploadBtn.addEventListener('click', () => fileInput.click());
    fileInput.addEventListener('change', () => {
      void ingestUploadFiles(Array.from(fileInput.files ?? []));
      // Re-selecting the same file must re-fire change.
      fileInput.value = '';
    });

    const dragHasFiles = (e: DragEvent): boolean =>
      Array.from(e.dataTransfer?.types ?? []).includes('Files');
    // dragleave fires on every crossing into a child element; a depth
    // counter is the standard fix for the resulting highlight flicker.
    let dragDepth = 0;
    dropzone.addEventListener('dragenter', (e) => {
      if (!dragHasFiles(e)) return;
      e.preventDefault();
      dragDepth += 1;
      if (mgr.remaining() > 0 && !fileInput.disabled) {
        dropzone.classList.add('is-dragover');
      }
    });
    dropzone.addEventListener('dragover', (e) => {
      if (!dragHasFiles(e)) return;
      // Without preventDefault the browser navigates to the dropped file.
      e.preventDefault();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = mgr.remaining() > 0 && !fileInput.disabled ? 'copy' : 'none';
      }
    });
    dropzone.addEventListener('dragleave', (e) => {
      if (!dragHasFiles(e)) return;
      dragDepth = Math.max(0, dragDepth - 1);
      if (dragDepth === 0) dropzone.classList.remove('is-dragover');
    });
    dropzone.addEventListener('drop', (e) => {
      e.preventDefault();
      dragDepth = 0;
      dropzone.classList.remove('is-dragover');
      void ingestUploadFiles(Array.from(e.dataTransfer?.files ?? []));
    });

    panel.addEventListener('paste', onPaste);
  }

  const unsubscribe = mgr.subscribe(render);
  render();

  // Stop subscribing when this section is removed from the DOM. There's no
  // unmount hook in the plain-DOM widget, so use a MutationObserver on the
  // panel root: when the attachments section disappears (form re-render or
  // success state), unsubscribe.
  const observer = new MutationObserver(() => {
    if (!panel.contains(slotsEl)) {
      unsubscribe();
      // #605 — the paste listener lives on the panel element itself, which
      // survives re-renders; remove it here or each re-wire stacks another.
      panel.removeEventListener('paste', onPaste);
      observer.disconnect();
    }
  });
  observer.observe(panel, { childList: true, subtree: true });

  captureBtn.addEventListener('click', () => {
    void captureCurrentPage(mgr, panel, captureBtn, privacyConfig?.maskSelectors, messages);
  });

  // #215 — Pin button click enters pin mode via a PinController. The
  // controller mounts a fullscreen event-target overlay on the host page,
  // tracks pointer/hover, captures the clicked element + a full-page
  // screenshot, then disposes the overlay. We add the screenshot as an
  // attachment and stash the pin data in a module-level state that the
  // submit handler reads.
  if (pinBtn) {
    pinBtn.addEventListener('click', () => {
      if (pinBtn.dataset.busy === 'true') return;
      pinBtn.dataset.busy = 'true';
      // Visually hide widget panel during pin mode so the user can see
      // the entire host page. Restored on dispose.
      const widgetHost = document.getElementById('oncatch-widget-host') as HTMLElement | null;
      const prevHostDisplay = widgetHost?.style.display ?? '';
      if (widgetHost) widgetHost.style.display = 'none';

      const restore = () => {
        if (widgetHost) widgetHost.style.display = prevHostDisplay;
        delete pinBtn.dataset.busy;
        // Trigger render() to re-evaluate button disabled state — render()
        // subscribes to mgr changes, so a no-op set triggers it.
        // Cheap trick: re-emit by calling getSlots() (a public read);
        // the render() body re-runs because we explicitly drive it here.
        render();
      };

      const maskSelectors = combineMaskSelectors(privacyConfig?.maskSelectors);

      const controller = new PinController({
        captureScreenshot: async () => {
          // Reuse the existing screenshot pipeline: html-to-image on the
          // full document. The pin's rect is stored separately so the
          // admin can render an overlay on top of this image.
          const maskTeardown = applyScreenshotMask(maskSelectors);
          try {
            const htmlToImage = await import('html-to-image');
            const blob = await htmlToImage.toBlob(document.documentElement, {
              pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
              skipFonts: false,
              cacheBust: false,
            });
            if (!blob) throw new Error('html-to-image returned null blob');
            return blob;
          } finally {
            maskTeardown();
          }
        },
        onCapture: ({ pinned, screenshotBlob }) => {
          restore();
          // Add the screenshot to the attachment manager as a regular
          // screenshot slot — same path as the Take a screenshot button.
          const filename = `pin-${new Date().toISOString().replace(/[:.]/g, '-')}.png`;
          const file = new File([screenshotBlob], filename, { type: 'image/png' });
          // #333 — widget-produced PNG; skip the WebP re-encode round-trip.
          void mgr.addFile(file, filename, { skipImageCompression: true });
          // Stash pin data for the submit handler.
          activePin = pinned;
        },
        onCancel: () => {
          restore();
        },
        onCaptureError: (err) => {
          restore();
          showError(panel, 'Pin capture failed. Try again or use Take a screenshot.');
          // biome-ignore lint/suspicious/noConsole: dev-only diagnostic
          console.warn('[oncatch] pin capture error:', err);
        },
      });
      controller.enter();
    });
  }

  regionBtn.addEventListener('click', () => {
    void captureSelectedRegion(mgr, panel, regionBtn, privacyConfig?.maskSelectors, messages);
  });

  if (recordBtn) {
    recordBtn.addEventListener('click', () => {
      void runRecordingFlowFromButton(
        mgr,
        panel,
        recordBtn,
        recordingContext,
        onWidgetEvent,
        messages,
        locale,
        () => captureAvailability,
        hosted,
      );
    });
  }
}

/**
 * #163 PR B — handles the Record button click: lazy-imports recording-flow,
 * runs the consent → record → stop pipeline, and adds the result to the
 * AttachmentManager via addRecordingResult on success. Cancel at any step
 * (consent decline, screen-pick dismiss, manual cancel) is a no-op for
 * the slot list.
 *
 * #770 — the button opens a mode chooser before asking for any browser
 * permission. Screen, camera-overlay, and microphone-only recordings then
 * each receive a capture-specific consent disclosure.
 *
 * Workspace name + retention days are hardcoded fallbacks pending a
 * widget-config response extension that plumbs the workspace's display
 * name and current `attachment_retention_days` through to the widget.
 * Once that lands, replace the hardcoded values with the configured ones
 * — the consent modal copy needs the real workspace name for compliance.
 */
async function runRecordingFlowFromButton(
  mgr: AttachmentManager,
  panel: HTMLElement,
  recordBtn: HTMLButtonElement,
  recordingContext?: { workspaceName?: string; retentionDays?: number },
  onWidgetEvent?: UiHandlers['onWidgetEvent'],
  messages?: WidgetMessages,
  locale?: SupportedLocale,
  availability?: () => CaptureChoiceAvailability,
  hosted?: {
    widgetBaseUrl?: string;
    apiBaseUrl?: string;
    getToken?: () => Promise<string | null>;
    workspaceId?: string;
  },
): Promise<void> {
  if (mgr.remaining() === 0) {
    showError(panel, 'Maximum 5 attachments reached.');
    return;
  }
  if (recordingCount(mgr) >= 1) {
    showError(panel, 'Only one recording per submission.');
    return;
  }

  recordBtn.dataset.busy = 'true';
  recordBtn.disabled = true;
  const labelEl = recordBtn.querySelector('span');
  const originalLabel = labelEl?.textContent ?? '';
  if (labelEl) labelEl.textContent = messages?.captureLabelPreparing ?? 'Preparing…';

  // Hide any prior camera-denied notice so a fresh attempt starts clean.
  const cameraDeniedBanner = panel.querySelector<HTMLDivElement>('[data-att-camera-denied]');
  if (cameraDeniedBanner) cameraDeniedBanner.hidden = true;

  const shadow = panel.getRootNode();
  if (!(shadow instanceof ShadowRoot)) {
    if (labelEl) labelEl.textContent = originalLabel;
    recordBtn.disabled = false;
    delete recordBtn.dataset.busy;
    showError(panel, 'Recording unavailable in this context.');
    return;
  }

  try {
    const chooser = await import('./capture-choice-modal.ts');
    const mode = await chooser.showCaptureChoiceModal(
      shadow,
      availability?.() ?? { screen: false, camera: false, audio: false },
      messages,
    );
    if (!mode) return;
    const selectedAvailability = availability?.()[mode];
    if (
      selectedAvailability &&
      typeof selectedAvailability !== 'boolean' &&
      selectedAvailability.kind === 'hosted'
    ) {
      if (!hosted?.widgetBaseUrl || !hosted.apiBaseUrl || !hosted.getToken || !hosted.workspaceId) {
        throw new Error('Secure recorder is unavailable.');
      }
      let retryInFlight = false;
      const startHosted = async (): Promise<void> => {
        if (retryInFlight) return;
        retryInFlight = true;
        const hostedResult = await launchHostedRecorder({
          mode: mode as 'camera' | 'audio',
          workspaceId: hosted.workspaceId as string,
          widgetBaseUrl: hosted.widgetBaseUrl as string,
          apiBaseUrl: hosted.apiBaseUrl as string,
          workspaceName: recordingContext?.workspaceName ?? '',
          retentionDays: recordingContext?.retentionDays ?? 90,
          locale: locale ?? 'en',
          getToken: hosted.getToken as () => Promise<string | null>,
        });
        retryInFlight = false;
        if (!hostedResult.ok) {
          const errors = {
            popup_blocked: 'Your browser blocked the secure recorder. Allow popups for this site.',
            timed_out: 'The secure recorder did not open. Allow popups for this site.',
            unavailable: 'Recording is not available in this browser.',
            cancelled: '',
            failed: 'The secure recorder could not be opened. Try again.',
          } as const;
          const error = errors[hostedResult.reason];
          if (error) {
            if (hostedResult.reason === 'popup_blocked' || hostedResult.reason === 'timed_out') {
              showHostedPopupRecovery(panel, error, () => void startHosted());
            } else {
              showError(panel, error);
            }
          }
          return;
        }
        if (mode === 'audio') {
          await mgr.addAudioRecordingResult({
            blob: hostedResult.blob,
            filename: hostedResult.filename,
            contentType: hostedResult.contentType as 'audio/mp4' | 'audio/webm',
          });
        } else {
          await mgr.addRecordingResult({
            blob: hostedResult.blob,
            filename: hostedResult.filename,
            contentType: hostedResult.contentType as 'video/mp4' | 'video/webm',
            attachmentType: 'video_with_camera',
          });
        }
      };
      await startHosted();
      return;
    }
    const mod = await import('./recording-flow.ts');
    if (labelEl) labelEl.textContent = originalLabel;
    // #241: workspaceName + retentionDays come from /api/v1/widget-config.
    // Empty / missing values fall back to neutral copy ("this site" / 90 days)
    // so the modal still reads coherently when the workspace has no name set
    // or when an old widget bundle (pre-#241) hits a new server.
    const consentContext = {
      shadow,
      workspaceName:
        recordingContext?.workspaceName && recordingContext.workspaceName.length > 0
          ? recordingContext.workspaceName
          : '',
      retentionDays: recordingContext?.retentionDays ?? 90,
      onAuditEvent: onWidgetEvent,
      messages,
      locale,
    };

    // Consent FIRST — no capture API is called before this explicit disclosure.
    if (mode === 'audio') {
      const audioMod = await import('./audio-recording-flow.ts');
      const result = await audioMod.runAudioRecordingFlow(consentContext);
      if (!result) return;
      await mgr.addAudioRecordingResult(result);
      return;
    }
    const consent = await mod.showConsentModal({
      ...consentContext,
      cameraDisclosure: mode === 'camera',
    });
    if (!consent.accepted) return;
    onWidgetEvent?.({ type: 'recording_consent_accepted' });

    if (mode === 'camera') {
      const cameraMod = await import('./camera-recording-flow.ts');
      const result = await cameraMod.runCameraRecordingFlow({
        ...consentContext,
        skipConsent: true,
      });
      if (!result) return;
      // Partial fulfillment: the reporter agreed to camera but the prompt was
      // denied or the stream died. The screen recording is still good, so keep
      // it and surface why the camera is missing.
      if (!result.cameraCaptured && cameraDeniedBanner) {
        cameraDeniedBanner.hidden = false;
      }
      await mgr.addRecordingResult({
        blob: result.blob,
        filename: result.filename,
        contentType: result.contentType as 'video/mp4' | 'video/webm',
        attachmentType: 'video_with_camera',
      });
      return;
    }

    const result = await mod.runRecordingFlow({ ...consentContext, skipConsent: true });
    if (!result) return;
    await mgr.addRecordingResult({
      blob: result.blob,
      filename: result.filename,
      contentType: result.contentType as 'video/mp4' | 'video/webm',
    });
  } catch (err) {
    showError(panel, err instanceof Error ? err.message : 'Recording failed');
  } finally {
    if (labelEl) labelEl.textContent = originalLabel;
    recordBtn.disabled = false;
    delete recordBtn.dataset.busy;
  }
}

/**
 * Production AttachmentManager supplies countRecordings(). Keep the legacy
 * video fallback for lightweight embed/test adapters that predate #770;
 * deployed widgets always take the audio-aware branch.
 */
function recordingCount(mgr: AttachmentManager): number {
  const maybeAudioAware = mgr as AttachmentManager & { countRecordings?: () => number };
  return typeof maybeAudioAware.countRecordings === 'function'
    ? maybeAudioAware.countRecordings()
    : mgr.countVideos();
}

/**
 * #533 — put the submit button into a busy state with a REAL animated spinner
 * beside the label. Built from DOM nodes rather than innerHTML so it needs no
 * Trusted Types policy round trip.
 */
function setSubmitBusy(btn: HTMLButtonElement, label: string): void {
  btn.disabled = true;
  btn.setAttribute('aria-busy', 'true');
  btn.textContent = '';
  const spinner = document.createElement('span');
  spinner.className = 'submit-spinner';
  spinner.setAttribute('aria-hidden', 'true');
  btn.appendChild(spinner);
  btn.appendChild(document.createTextNode(label));
}

/** #533 — inverse of setSubmitBusy. Drops the spinner and restores the label. */
function restoreSubmitButton(btn: HTMLButtonElement, label: string): void {
  btn.disabled = false;
  btn.removeAttribute('aria-busy');
  btn.textContent = label;
}

/**
 * #533 — disable/re-enable every control in the form while a submission is in
 * flight. Before this, only the submit button changed, so the reporter could
 * still add or remove attachments between prepareForSubmit() and the POST.
 *
 * Restore is state-aware: a control that was ALREADY disabled when the
 * submission started (e.g. a capture button the attachment renderer gated off
 * because the slot cap is full) stays disabled afterwards. Blindly re-enabling
 * everything would resurrect controls the renderer had deliberately turned off,
 * and there is no guaranteed re-render to correct it.
 */
function setFormBusy(form: HTMLFormElement, busy: boolean): void {
  form.dataset.submitting = busy ? 'true' : 'false';
  const controls = form.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLButtonElement
  >('input, textarea, button');
  for (const el of Array.from(controls)) {
    if (busy) {
      if (el.disabled) {
        el.dataset['ocWasDisabled'] = '1';
      } else {
        delete el.dataset['ocWasDisabled'];
      }
      el.disabled = true;
    } else {
      el.disabled = el.dataset['ocWasDisabled'] === '1';
      delete el.dataset['ocWasDisabled'];
    }
  }
}

function slotMarkup(slot: AttachmentSlot): string {
  const isFailed = slot.state === 'failed';
  const showSpinner =
    slot.state === 'reserving' || slot.state === 'uploading' || slot.state === 'compressing';
  const isQueued = slot.state === 'queued';
  const overlayLabel = isQueued
    ? 'Queued'
    : slot.state === 'reserving'
      ? 'Reserving'
      : slot.state === 'uploading'
        ? slot.backgroundProcessing
          ? 'Sending'
          : 'Scanning'
        : 'Compressing';
  // #334 — when ffmpeg reports a 0..1 fraction during a video encode, show a
  // determinate percentage instead of the indefinite spinner. Legacy cores
  // never emit progress, so compressionProgress stays undefined and we fall
  // back to the spinner (AC3). Only meaningful in the `compressing` state.
  const showProgress = slot.state === 'compressing' && slot.compressionProgress !== undefined;
  const progressPct = showProgress ? Math.round((slot.compressionProgress ?? 0) * 100) : 0;

  // #536 — the in-progress indicator is gated on THUMBNAIL READINESS, not on
  // network state.
  //
  // makeThumbnail() is pure local canvas work that finishes in milliseconds,
  // while the reserve + proxy network phase takes seconds. Dimming the
  // already-rendered preview behind a full-cover overlay for that whole phase
  // made a working widget look stuck — the reporter's screenshot was ready and
  // hidden from them.
  //
  // Thumbnail present → show it unobscured with a small corner badge.
  // No thumbnail yet (every video; images pre-thumbnail) → today's covering
  // overlay, unchanged. The #334 determinate progress variant is inherently in
  // that branch because thumbnails are generated after compression.
  const hasThumb = Boolean(slot.thumbnailDataUrl);
  const showCoveringOverlay = (showSpinner || isQueued) && !hasThumb;
  const showCornerBadge = (showSpinner || isQueued) && hasThumb;

  return `
    <div class="att-slot ${isFailed ? 'is-failed' : ''}" title="${escapeAttr(slot.filename)}">
      ${
        slot.thumbnailDataUrl
          ? `<img class="att-slot-thumb" src="${escapeAttr(slot.thumbnailDataUrl)}" alt="${escapeAttr(slot.filename)}" />`
          : `<svg class="att-slot-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>`
      }
      ${slot.contentType === 'text/plain' ? `<span class="att-slot-file-name">${escapeHtml(slot.filename)}</span>` : ''}
      ${
        showProgress && !hasThumb
          ? `<div class="att-slot-overlay" data-att-slot-progress><div class="att-slot-progress-pct" role="status" aria-label="Compressing ${progressPct}%" aria-live="polite">${progressPct}%</div><div class="att-slot-progress-track"><div class="att-slot-progress-fill" style="width:${progressPct}%"></div></div></div>`
          : showCoveringOverlay
            ? `<div class="att-slot-overlay">${showSpinner ? `<div class="att-slot-spinner" aria-hidden="true"></div>` : ''}<span role="status">${overlayLabel}</span></div>`
            : showCornerBadge
              ? `<div class="att-slot-badge">${showSpinner ? `<div class="att-slot-spinner" aria-hidden="true"></div>` : ''}<span role="status">${overlayLabel}</span></div>`
              : ''
      }
      ${
        isFailed
          ? `<button type="button" class="att-slot-retry" data-att-slot-action="retry" data-att-slot-id="${escapeAttr(slot.id)}" aria-label="Retry upload">Retry</button>
             <div class="att-slot-error-msg" role="alert">${escapeHtml(slot.errorMessage ?? 'Upload failed')}</div>`
          : ''
      }
      <button type="button" class="att-slot-action" data-att-slot-action="remove" data-att-slot-id="${escapeAttr(slot.id)}" aria-label="Remove ${escapeAttr(slot.filename)}">×</button>
    </div>
  `;
}

/**
 * #161 Phase 1: capture the current customer page as a PNG and add it as a slot.
 *
 * Uses html-to-image (lazy-loaded ~25KB chunk) to walk the DOM and rasterize
 * to a canvas. The widget's own UI is invisible to the capture for free —
 * html-to-image doesn't walk shadow DOM at all (open or closed), so the panel
 * + button + everything inside our shadow root are naturally excluded. The
 * host element itself is `<div style="all:initial">` (display: inline, no
 * content) so it's a no-op in the rendered output.
 *
 * Earlier versions hid the host via display:none during capture; this caused
 * the widget panel to vanish for 1-2s on every click and made it look like
 * the app had crashed. Removed 2026-05-01 — the closed shadow DOM gives us
 * the invisibility we need without UX cost.
 */
async function captureCurrentPage(
  mgr: AttachmentManager,
  panel: HTMLElement,
  captureBtn: HTMLButtonElement,
  privacyMaskExtras?: ReadonlyArray<string>,
  messages?: WidgetMessages,
): Promise<void> {
  if (mgr.remaining() === 0) {
    showError(panel, 'Maximum 5 screenshots reached.');
    return;
  }

  // Mark busy so render() disables the button on the next mgr.notify().
  // The capturing label is the user's progress signal — the widget itself
  // stays fully visible throughout (panel doesn't disappear).
  captureBtn.dataset.busy = 'true';
  captureBtn.disabled = true;
  const originalLabel = captureBtn.querySelector('span')?.textContent ?? '';
  const labelEl = captureBtn.querySelector('span');
  if (labelEl) labelEl.textContent = messages?.captureLabelCapturing ?? 'Capturing…';

  // Fade the widget host to 25% opacity (75% transparent) as a visual cue that
  // the widget itself isn't in the captured screenshot. Was 50% — bumped per
  // Brian 2026-05-01: at 50% the widget barely looked transparent, so the
  // signal didn't read. Restore on completion via the finally block. The host
  // has no transition by default; set one inline so both the fade and the
  // restore animate smoothly.
  const host = document.getElementById('oncatch-widget-host') as HTMLElement | null;
  const prevHostOpacity = host?.style.opacity ?? '';
  const prevHostTransition = host?.style.transition ?? '';
  if (host) {
    host.style.transition = 'opacity 150ms ease';
    host.style.opacity = '0.25';
  }

  // #217: visual privacy masking — inject a <style> that blanks out
  // password / email / [data-oncatch-mask] elements (plus any workspace-
  // configured selectors) for the duration of html-to-image's traversal.
  // Teardown removes the style in the finally block so customer page UX
  // is restored regardless of capture outcome.
  const maskTeardown = applyScreenshotMask(combineMaskSelectors(privacyMaskExtras));

  try {
    // Dynamic import for forward-compatibility with future code-splitting.
    // NOTE: the widget currently builds as a single IIFE (vite.config.ts
    // formats: ['iife']), which inlines all dynamic imports into the main
    // bundle — so this is an eager import in practice. When we eventually
    // switch the widget to ESM output (or add a custom CDN-based chunk
    // loader), this site becomes truly lazy without a code change.
    const htmlToImage = await import('html-to-image');

    const blob = await htmlToImage.toBlob(document.documentElement, {
      // Preserve the device pixel ratio so high-DPI screens look sharp,
      // but cap at 2 to keep blob size reasonable.
      pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
      // Skip cross-origin images we can't embed; show empty box instead of
      // throwing the whole capture.
      skipFonts: false,
      cacheBust: false,
    });

    if (!blob) {
      showError(panel, 'Could not capture the page.');
      return;
    }

    const url = (typeof location !== 'undefined' ? location.pathname : '') || 'page';
    const slug =
      url
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 60) || 'page';
    const filename = `${slug}-${new Date().toISOString().replace(/[:.]/g, '-')}.png`;

    // #210: restore host visibility + tear down the mask BEFORE the
    // annotation modal opens — otherwise the modal renders inside a
    // dimmed shadow host and is invisible. The `finally` block below
    // re-runs idempotent restorations as defense-in-depth.
    maskTeardown();
    if (host) host.style.opacity = prevHostOpacity;

    const result = messages
      ? await runAnnotationFlow(blob, panel, messages, makePanelCloseSubscriber(panel), captureBtn)
      : ({ outcome: 'skip', blob } satisfies AnnotationOutcome);
    if (result.outcome === 'save' || result.outcome === 'skip') {
      // #333 — widget-produced PNG; skip the WebP re-encode round-trip.
      // Capture and annotation are complete; the slot owns queued scanning and its errors.
      void mgr.addFile(result.blob, filename, { skipImageCompression: true }).catch(() => {
        showError(panel, 'Could not attach the captured image. Try again.');
      });
    }
    // outcome === 'discard' or 'error' → user closed mid-flow or render
    // crashed; skip addFile entirely. They can recapture.
  } catch (err) {
    const reason = err instanceof Error ? err.message : '';
    showError(
      panel,
      reason.includes('CORS') || reason.includes('tainted')
        ? 'Could not capture: the page contains cross-origin images we can’t embed.'
        : 'Could not capture the page. Try again.',
    );
  } finally {
    // #217: always remove the masking style, even if html-to-image threw —
    // leaving the style attached would permanently break the customer's
    // page UX (passwords stay visually blanked even after the widget closes).
    // #210 idempotent re-run: the try-block already restored on the happy
    // path; this catches the case where html-to-image threw before that.
    maskTeardown();
    if (host) {
      host.style.opacity = prevHostOpacity;
      // Leave the transition rule in place briefly so the fade-back animates,
      // then clear it so we don't leak inline transition state. 200ms covers
      // the 150ms fade with a small buffer.
      window.setTimeout(() => {
        if (host) host.style.transition = prevHostTransition;
      }, 200);
    }
    captureBtn.dataset.busy = 'false';
    captureBtn.disabled = mgr.remaining() === 0;
    if (labelEl) labelEl.textContent = originalLabel;
  }
}

// #210: build a subscribeClose lambda for the annotation flow. Bridges the
// shadow-root CustomEvent (dispatched in mountUi's close handlers) to the
// annotation flow's subscribe/unsubscribe contract.
function makePanelCloseSubscriber(panel: HTMLElement): (cb: () => void) => () => void {
  return (cb: () => void) => {
    const root = panel.getRootNode();
    if (!(root instanceof ShadowRoot)) return () => undefined;
    const handler = () => cb();
    root.addEventListener(PANEL_CLOSE_EVENT, handler);
    return () => root.removeEventListener(PANEL_CLOSE_EVENT, handler);
  };
}

/**
 * #331 — "Capturing region…" overlay shown between mouse-release and the
 * screenshot being attached. Before this, the widget host went
 * `visibility: hidden` for the whole capture and the user saw nothing for a
 * few seconds on complex pages — it looked broken even when working.
 *
 * The overlay mounts its own host with a CLOSED shadow root. html-to-image
 * never walks shadow DOM (open or closed) — the same property that keeps the
 * widget itself out of captures — so this overlay is guaranteed absent from
 * the screenshot (AC2) without any show/hide dance around the toCanvas call.
 * Positioned bottom-right where the widget normally sits. Returns a teardown
 * that the caller MUST invoke on completion AND on error (AC3).
 */
function showCapturingRegionOverlay(label: string): () => void {
  if (typeof document === 'undefined') return () => undefined;

  const host = document.createElement('div');
  host.id = 'oncatch-capturing-overlay';
  host.style.cssText = 'all: initial;';
  // The live status (role=status, aria-live) lives inside the closed shadow
  // root below — same isolation pattern as the widget host itself.
  const shadow = host.attachShadow({ mode: 'closed' });

  const box = document.createElement('div');
  box.setAttribute('role', 'status');
  box.setAttribute('aria-live', 'polite');
  box.style.cssText = [
    'position: fixed',
    'right: 20px',
    'bottom: 20px',
    'z-index: 2147483647',
    'display: flex',
    'align-items: center',
    'gap: 10px',
    'padding: 12px 16px',
    'border-radius: 10px',
    'background: #1b1a17',
    'color: #fdfcf9',
    'font: 500 13px system-ui, -apple-system, sans-serif',
    'box-shadow: 0 6px 24px rgba(0, 0, 0, 0.28)',
    'pointer-events: none',
  ].join(';');

  const spinner = document.createElement('div');
  spinner.style.cssText = [
    'width: 16px',
    'height: 16px',
    'border: 2px solid rgba(253, 252, 249, 0.3)',
    'border-top-color: #fdfcf9',
    'border-radius: 50%',
    'animation: oncatch-capturing-spin 0.7s linear infinite',
  ].join(';');

  // #584: adopted, not a <style> element — an un-nonced <style> is refused by a
  // host `style-src-elem` policy, which left the spinner frozen. The box + dot
  // themselves use inline style attributes (a different directive, permitted on
  // the hosts we've seen), so only the keyframes were lost.
  const releaseStyles = adoptStyles(
    shadow,
    '@keyframes oncatch-capturing-spin { to { transform: rotate(360deg); } }',
  );

  const text = document.createElement('span');
  text.textContent = label;

  box.appendChild(spinner);
  box.appendChild(text);
  shadow.appendChild(box);
  document.body.appendChild(host);

  let torn = false;
  return () => {
    if (torn) return;
    torn = true;
    releaseStyles();
    host.remove();
  };
}

/**
 * #161 Phase 1: capture a user-selected region of the page.
 *
 * Closes the panel (keeping just the bug button visible bottom-right), shows
 * a fullscreen crosshair overlay, lets the user drag a rectangle, then (since
 * #332) rasterizes just the VIEWPORT via html-to-image and crops to the
 * selection in canvas. ESC or click-without-drag cancels. Selections smaller than
 * 20x20 CSS pixels are treated as cancellation (avoids accidental tiny
 * captures on a misclick).
 *
 * Visual feedback (per Brian 2026-05-01):
 *   - During selection AND capture: the widget host is fully hidden
 *     (visibility: hidden) so the bug button doesn't visually compete with
 *     the crosshair drag and can't be accidentally selected as part of the
 *     captured rectangle. Earlier behavior was an opacity fade with overlap
 *     detection — too subtle, and the button was still grabbable.
 *   - On completion / cancel: visibility restores.
 */
async function captureSelectedRegion(
  mgr: AttachmentManager,
  panel: HTMLElement,
  regionBtn: HTMLButtonElement,
  privacyMaskExtras?: ReadonlyArray<string>,
  messages?: WidgetMessages,
): Promise<void> {
  if (mgr.remaining() === 0) {
    showError(panel, 'Maximum 5 screenshots reached.');
    return;
  }

  regionBtn.dataset.busy = 'true';
  regionBtn.disabled = true;

  // Hide the host entirely for the duration of region selection + capture.
  // visibility:hidden (not display:none) preserves the host's layout slot —
  // the host is `<div style="all:initial">` (inline, no content) so layout
  // impact is zero either way, but visibility:hidden is the safer default.
  const host = document.getElementById('oncatch-widget-host') as HTMLElement | null;
  const prevHostVisibility = host?.style.visibility ?? '';
  if (host) host.style.visibility = 'hidden';

  // Track whether the panel was open so we can restore on completion.
  const panelWasOpen = !panel.hidden;
  if (panelWasOpen) panel.hidden = true;

  // Block scroll while selecting so coordinate math stays simple.
  const bodyPrevOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  const rect = await runRegionPicker(messages);

  // Restore scroll regardless of cancel/proceed.
  document.body.style.overflow = bodyPrevOverflow;

  // #217: visual privacy masking — same as captureCurrentPage. Inject the
  // masking style INSIDE the try so a region-picker cancel skips the work
  // entirely, and tear it down in the finally regardless of outcome.
  let maskTeardown: () => void = () => undefined;
  // #331: progress overlay shown during the rasterize step. Declared here so
  // the finally can tear it down on any exit path (incl. early returns).
  let overlayTeardown: () => void = () => undefined;

  try {
    if (!rect) return; // user cancelled (ESC or no-drag click)

    // #331: surface a "Capturing region…" overlay now that the user has
    // released the drag. Without it the host is hidden and the page looks
    // frozen during the html-to-image rasterize. Closed shadow root keeps it
    // out of the capture (AC2); torn down before the annotation modal opens
    // and again in the finally (AC3).
    overlayTeardown = showCapturingRegionOverlay(
      messages?.captureRegionCapturing ?? 'Capturing region…',
    );

    maskTeardown = applyScreenshotMask(combineMaskSelectors(privacyMaskExtras));

    // The widget is invisible to the capture either way (html-to-image doesn't
    // walk shadow DOM), but we keep visibility:hidden until the very end so
    // the user sees a clean page during the brief html-to-image step.
    const htmlToImage = await import('html-to-image');
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);

    // #332 — capture ONLY the viewport, not the whole document. The selection
    // is in viewport coords, so the visible area is always where it lives.
    // plan.captureOptions caps html-to-image's surface to the viewport + shifts
    // the on-screen slice to the canvas origin; plan.cropSource then maps the
    // selection straight through with no scroll offset.
    //
    // #329 — the rasterize + crop + emptiness checks moved into
    // renderRegionCapture so the real-browser regression harness drives the
    // SAME code that ships, and so a capture that rasterized to nothing can
    // never reach addFile(). See region-capture.ts for the math + the guard.
    const outcome = await renderRegionCapture(
      {
        selection: rect,
        viewport: { width: window.innerWidth, height: window.innerHeight },
        scroll: { x: window.scrollX, y: window.scrollY },
        pixelRatio,
      },
      {
        toCanvas: (node, options) => htmlToImage.toCanvas(node, options),
        root: document.documentElement,
        createCanvas: () => document.createElement('canvas'),
      },
    );

    if (!outcome.ok) {
      // #329 AC4 — the numbers that make a repeat diagnosable from a customer's
      // console: what html-to-image actually produced, and the rectangle we
      // copied out of it. Every one of these looked correct while the image
      // itself was empty, which is exactly why the original report had to be
      // narrowed down by file size.
      // biome-ignore lint/suspicious/noConsole: single diagnostic on a failed capture
      console.warn('[oncatch] region capture failed:', outcome.reason, outcome.diagnostics);
      showError(
        panel,
        outcome.reason === 'no_2d_context'
          ? 'Could not crop region.'
          : // Deliberately does not quote the other button's label — the capture
            // buttons are localised and this string (like every other error in
            // this function) is not.
            'The capture came out empty. Try again, or capture the whole page instead.',
      );
      return;
    }

    const blob = await new Promise<Blob | null>((resolve) =>
      outcome.canvas.toBlob((b) => resolve(b), 'image/png'),
    );
    if (!blob) {
      showError(panel, 'Could not capture region.');
      return;
    }

    const url = (typeof location !== 'undefined' ? location.pathname : '') || 'page';
    const slug =
      url
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 60) || 'page';
    const filename = `${slug}-region-${new Date().toISOString().replace(/[:.]/g, '-')}.png`;

    // #210: restore everything region capture hid (mask, host visibility,
    // panel hidden state) BEFORE the annotation modal opens — otherwise
    // it renders inside a hidden host and the user can't see it. The
    // `finally` block re-runs these idempotently as defense-in-depth.
    // #331: tear down the capturing overlay too — the capture is done and the
    // annotation modal (or the attached slot) is the next visible state.
    overlayTeardown();
    maskTeardown();
    if (host) host.style.visibility = prevHostVisibility;
    if (panelWasOpen) panel.hidden = false;

    const result = messages
      ? await runAnnotationFlow(blob, panel, messages, makePanelCloseSubscriber(panel), regionBtn)
      : ({ outcome: 'skip', blob } satisfies AnnotationOutcome);
    if (result.outcome === 'save' || result.outcome === 'skip') {
      // #333 — widget-produced PNG; skip the WebP re-encode round-trip.
      // Capture and annotation are complete; the slot owns queued scanning and its errors.
      void mgr.addFile(result.blob, filename, { skipImageCompression: true }).catch(() => {
        showError(panel, 'Could not attach the captured image. Try again.');
      });
    }
  } catch (err) {
    const reason = err instanceof Error ? err.message : '';
    showError(
      panel,
      reason.includes('CORS') || reason.includes('tainted')
        ? 'Could not capture: the page contains cross-origin images we can’t embed.'
        : 'Could not capture region. Try again.',
    );
  } finally {
    // #331: idempotent overlay teardown — covers the error path + any early
    // return (no-blob, no-ctx, empty-canvas) where the happy-path teardown
    // above never ran. Safe to call twice (guarded internally).
    overlayTeardown();
    maskTeardown();
    if (host) host.style.visibility = prevHostVisibility;
    if (panelWasOpen) panel.hidden = false;
    regionBtn.dataset.busy = 'false';
    regionBtn.disabled = mgr.remaining() === 0;
  }
}

interface PickedRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Show a fullscreen crosshair overlay and resolve with the rectangle the user
 * dragged out, or null if they cancelled (ESC or click-without-drag or
 * sub-20px selection).
 */
function runRegionPicker(messages?: WidgetMessages): Promise<PickedRect | null> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.id = 'oncatch-region-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-label', 'Select a region of the page to capture');
    overlay.style.cssText = [
      'position: fixed',
      'inset: 0',
      'z-index: 2147483647',
      'cursor: crosshair',
      'user-select: none',
      'background: rgba(0, 0, 0, 0.35)',
    ].join(';');

    const help = document.createElement('div');
    help.style.cssText = [
      'position: fixed',
      'top: 16px',
      'left: 50%',
      'transform: translateX(-50%)',
      'background: rgba(0, 0, 0, 0.85)',
      'color: white',
      'padding: 8px 16px',
      'border-radius: 6px',
      'font: 500 13px system-ui, -apple-system, sans-serif',
      'pointer-events: none',
      'z-index: 2147483647',
    ].join(';');
    help.textContent =
      messages?.captureRegionHelp ?? 'Click and drag to select a region · Press Esc to cancel';

    const dimsLabel = document.createElement('div');
    dimsLabel.style.cssText = [
      'position: fixed',
      'background: white',
      'color: #1b1a17',
      'padding: 3px 8px',
      'border-radius: 4px',
      'font: 600 11px ui-monospace, SFMono-Regular, Menlo, monospace',
      'box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25)',
      'pointer-events: none',
      'z-index: 2147483647',
      'display: none',
    ].join(';');

    let selBox: HTMLDivElement | null = null;
    let startX = 0;
    let startY = 0;
    let dragging = false;

    document.body.appendChild(overlay);
    document.body.appendChild(help);
    document.body.appendChild(dimsLabel);

    const cleanup = () => {
      overlay.remove();
      help.remove();
      dimsLabel.remove();
      window.removeEventListener('keydown', onKey);
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      dragging = true;
      startX = e.clientX;
      startY = e.clientY;

      // Once a drag starts, replace the dim overlay with a "spotlight" effect
      // — the selection rectangle has its own outset shadow that dims
      // everything outside it.
      overlay.style.background = 'transparent';

      selBox = document.createElement('div');
      selBox.style.cssText = [
        'position: fixed',
        `left: ${startX}px`,
        `top: ${startY}px`,
        'width: 0px',
        'height: 0px',
        'border: 2px solid #c2410c',
        'background: rgba(194, 65, 12, 0.08)',
        'box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.45)',
        'pointer-events: none',
        'z-index: 2147483647',
      ].join(';');
      overlay.appendChild(selBox);

      help.style.display = 'none';
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!dragging || !selBox) return;
      const x = Math.min(startX, e.clientX);
      const y = Math.min(startY, e.clientY);
      const w = Math.abs(e.clientX - startX);
      const h = Math.abs(e.clientY - startY);
      selBox.style.left = `${x}px`;
      selBox.style.top = `${y}px`;
      selBox.style.width = `${w}px`;
      selBox.style.height = `${h}px`;

      dimsLabel.textContent = `${w} × ${h}`;
      dimsLabel.style.display = 'block';
      // Place dimensions label at the bottom-right of the selection, but
      // flip to inside if it would clip the viewport edge.
      const labelX = x + w + 8;
      const labelY = y + h + 8;
      dimsLabel.style.left = `${Math.min(labelX, window.innerWidth - 60)}px`;
      dimsLabel.style.top = `${Math.min(labelY, window.innerHeight - 24)}px`;
    };

    const onMouseUp = (e: MouseEvent) => {
      if (!dragging) {
        // No drag at all — treat as cancel.
        cleanup();
        resolve(null);
        return;
      }
      const x = Math.min(startX, e.clientX);
      const y = Math.min(startY, e.clientY);
      const w = Math.abs(e.clientX - startX);
      const h = Math.abs(e.clientY - startY);
      cleanup();
      if (w < 20 || h < 20) {
        // Tiny — treat as cancel rather than a misclick capture.
        resolve(null);
      } else {
        resolve({ x, y, w, h });
      }
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cleanup();
        resolve(null);
      }
    };

    overlay.addEventListener('mousedown', onMouseDown);
    overlay.addEventListener('mousemove', onMouseMove);
    overlay.addEventListener('mouseup', onMouseUp);
    window.addEventListener('keydown', onKey);
  });
}

function showAttachmentBlockerModal(
  panel: HTMLElement,
  mgr: AttachmentManager,
  onResolved: () => void,
): void {
  const failedCount = mgr.getSlots().filter((s) => s.state === 'failed').length;
  const modalHost = document.createElement('div');
  modalHost.className = 'modal';
  modalHost.setAttribute('role', 'dialog');
  modalHost.setAttribute('aria-modal', 'true');
  // #482: failedCount is a number; static markup otherwise. TT-safe assignment.
  setTrustedHtml(
    modalHost,
    `
    <h3>${failedCount === 1 ? 'One attachment failed to upload' : `${failedCount} attachments failed to upload`}</h3>
    <p>Choose how to continue:</p>
    <div class="modal-actions">
      <button type="button" class="submit" data-modal-action="retry">Retry uploads</button>
      <button type="button" class="submit-secondary" data-modal-action="proceed">Send without them</button>
      <button type="button" class="submit-secondary" data-modal-action="cancel">Cancel</button>
    </div>
  `,
  );
  panel.appendChild(modalHost);

  const close = () => {
    modalHost.remove();
    onResolved();
  };

  modalHost
    .querySelector<HTMLButtonElement>('[data-modal-action="retry"]')
    ?.addEventListener('click', () => {
      const failedIds = mgr
        .getSlots()
        .filter((s) => s.state === 'failed')
        .map((s) => s.id);
      Promise.all(failedIds.map((id) => mgr.retry(id).catch(() => undefined))).finally(close);
    });

  modalHost
    .querySelector<HTMLButtonElement>('[data-modal-action="proceed"]')
    ?.addEventListener('click', () => {
      const failedIds = mgr
        .getSlots()
        .filter((s) => s.state === 'failed')
        .map((s) => s.id);
      Promise.all(failedIds.map((id) => mgr.remove(id).catch(() => undefined))).finally(close);
    });

  modalHost
    .querySelector<HTMLButtonElement>('[data-modal-action="cancel"]')
    ?.addEventListener('click', close);
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

// Translates a snake_case error code from the OnCatch submission API or widget
// internals into a sentence shown to the bug reporter. Pre-#395 only 6 codes
// were mapped — the rest of the codes returned by /api/v1/submissions/route.ts
// (invalid_json, invalid_body, widget_misconfigured, insert_failed,
// reference_code_exhausted) leaked through as raw codes. This map covers every
// code the server-side route can produce, plus the widget-internal codes.
const WIDGET_ERROR_MESSAGES: Record<string, string> = {
  // Widget-internal failure modes
  widget_disabled_or_offline:
    "We couldn't send that report. Please refresh the page and try again.",
  no_token: 'Connection issue — please try again in a moment.',
  invalid_response: 'Connection issue — please try again in a moment.',
  challenge_failed: "Verification didn't succeed. Please refresh the page and try again.",
  attachment_ttl_failed: "An attachment couldn't be re-uploaded. Please remove it and try again.",
  attachment_credentials_changed: 'Your session changed. Your draft and files are preserved.',
  attachment_failed: 'One or more attachments failed. Please retry or remove them, then send.',
  // Codes returned by /api/v1/submissions/route.ts
  invalid_json: "We couldn't read your report. Please refresh the page and try again.",
  invalid_body: "We couldn't read your report. Please check the fields and try again.",
  widget_misconfigured:
    "This site's OnCatch widget isn't configured correctly. The site owner needs to fix it.",
  // Throttled by the per-IP submission cap (3/min, 20/hr). Says WAIT rather
  // than "try again", because trying again immediately is exactly what fails.
  rate_limited: "You're sending reports too quickly. Please wait a minute and try again.",
  insert_failed: "We couldn't save that report. Please try again in a moment.",
  reference_code_exhausted:
    'Something went wrong assigning a reference code. Please try again in a moment.',
};

function friendlyError(raw: string | undefined): string {
  if (!raw) return 'Sorry, something went wrong. Please try again.';
  return WIDGET_ERROR_MESSAGES[raw] ?? 'Sorry, something went wrong. Please try again.';
}

function showError(panel: HTMLElement, msg: string) {
  const el = panel.querySelector<HTMLDivElement>('.error');
  if (!el) return;
  el.textContent = msg;
  el.hidden = false;
}

/** #774 — a second explicit click qualifies as a user gesture for a new-tab retry. */
function showHostedPopupRecovery(panel: HTMLElement, message: string, retry: () => void): void {
  showError(panel, message);
  const error = panel.querySelector<HTMLDivElement>('.error');
  if (!error || error.querySelector('[data-oc-hosted-retry]')) return;
  const retryButton = document.createElement('button');
  retryButton.type = 'button';
  retryButton.dataset.ocHostedRetry = 'true';
  retryButton.textContent = 'Open secure recorder in a new tab';
  retryButton.style.marginLeft = '8px';
  retryButton.addEventListener('click', retry, { once: true });
  error.appendChild(retryButton);
}

/**
 * Exported only for #55 accessibility testing — see renderForm note.
 */
export function renderSuccess(
  panel: HTMLElement,
  referenceCode: string,
  handlers: UiHandlers,
  onReportAnother: () => void,
  partial?: { failed: number; total: number },
) {
  // #55 a11y: success state heading is h2 (matches panel-title's level — the
  // success state replaces the entire panel body, so h2 is the right
  // semantic level for the dialog's announcement). The heading carries
  // tabindex=-1 so the success-render path can shift programmatic focus
  // to it (see below) — without that, screen reader focus stays on the
  // submit button (now removed) and users get no spoken transition cue.
  // Strings come from the localised messages dictionary, not hardcoded.
  //
  // #256: when reconcile dropped some attachments, the partial-notice
  // paragraph renders BETWEEN the heading and body copy so the reporter
  // sees the partial outcome before the green "all done" cue. Without it,
  // a reporter whose video failed MIME-sniff sees pure success and the
  // video is silently gone.
  const m = handlers.messages;
  const partialNoticeHtml = partial
    ? `<p class="partial-notice" role="status">${escapeHtml(
        m.successPartialNotice(partial.failed, partial.total),
      )}</p>`
    : '';
  // #482: TT-safe assignment. All interpolated values escaped above/inline.
  setTrustedHtml(
    panel,
    `
    <div class="success" role="status" aria-live="polite">
      <h2 class="success-heading" tabindex="-1">${escapeHtml(m.successHeading)}</h2>
      ${partialNoticeHtml}
      <p>${escapeHtml(m.successCopy(referenceCode))}</p>
      <div class="ref">${escapeHtml(referenceCode)}</div>
      <div class="action-row">
        <button type="button" class="submit" data-action="report-another">${escapeHtml(m.successAnotherButton)}</button>
        <button type="button" class="submit-secondary" data-action="close">${escapeHtml(m.panelClose)}</button>
      </div>
    </div>
    <div class="footer">${escapeHtml(m.poweredBy)}</div>
  `,
  );
  // Move programmatic focus to the success heading so screen readers
  // announce the new state and keyboard users can Tab forward into the
  // action buttons from a known anchor.
  panel.querySelector<HTMLHeadingElement>('.success-heading')?.focus();
  panel
    .querySelector<HTMLButtonElement>('[data-action="report-another"]')
    ?.addEventListener('click', () => {
      onReportAnother();
      // After re-rendering the form, focus the first field so the user can keep typing.
      const firstInput = panel.querySelector<HTMLInputElement>('input[name=title]');
      firstInput?.focus();
    });
  panel.querySelector<HTMLButtonElement>('[data-action="close"]')?.addEventListener('click', () => {
    panel.hidden = true;
    handlers.onClose();
  });
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
